import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { postAdvisorChange } from '@/utils/advisorBus';
import { isLexUser } from '@/utils/lexDomain';
import { validateStnr } from '@/utils/lexSteuernummer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ArrowLeft, FileText, Receipt, Download, AlertCircle, AlertTriangle,
  ChevronLeft, ChevronRight, Check, X, Plus, Lock, Unlock, ClipboardCheck,
  FolderOpen, Inbox, Eye, MessageSquare, FileDown, CheckCircle2, Bell, History,
  ReceiptText, RefreshCw, FileCheck2, ArrowUp, ArrowDown, Boxes, LayoutDashboard,
  Building2, CalendarClock, Wallet, Mail, Phone, Ban, Copy, Plane, Scale,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import AdvisorFeedbackButton from '@/components/AdvisorFeedbackButton';
import CollabInbox from '@/components/CollabInbox';
import Tresor from '@/components/Tresor';
import PeriodClosureWizard from '@/components/PeriodClosureWizard';
import AdvisorVoranmeldungTab from '@/components/AdvisorVoranmeldungTab';
import PeriodCheckPanel from '@/components/advisor/PeriodCheckPanel';
import AdvisorInvestmentAllowanceControl from '@/components/advisor/AdvisorInvestmentAllowanceControl';
import AdvisorFixedAssetsTab from '@/components/anlagen/AdvisorFixedAssetsTab';
import ItemDetailDrawer from '@/components/ItemDetailDrawer';
import { CountryFeatureBadge } from '@/components/CountryFeatureBadge';
import { TaxProfileForm } from '@/components/TaxProfileForm';
import { AccountMappingTab } from '@/components/AccountMappingTab';
import CategoryHint from '@/components/CategoryHint';
import HelpOverlay, { useHelpOverlay, HelpButton } from '@/components/HelpOverlay';
import { getExpenseVatTotal, getInvoiceVatTotal } from '@/utils/vatCalc';
import { Checkbox } from '@/components/ui/checkbox';
import { BulkActionBar, ReviewProgress } from '@/components/advisor/ReviewWorkflow';
import AdvisorTravelSection from '@/components/advisor/AdvisorTravelSection';
import LexMandatTab from '@/components/lex/LexMandatTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function H() { return { Authorization: `Bearer ${localStorage.getItem('advisor_token')}` }; }
const fmt = (n) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtCur = (n) => `${fmt(n)} €`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('de-DE') : '—';

// 10 Leaf-Tabs (Juni 2026: Periodenabschlüsse ergänzt, „Tresor" → „Dokumente")
// IA-Reorganisation P1 (Feb 2026): Tab-Reihenfolge folgt der „statisch → dynamisch → funktional → audit"-Achse
const TABS = [
  { id: 'mandat', label: 'Mandat', icon: Scale, axis: 'static' }, // nur Kanzlei-Portal (lex)
  { id: 'overview', label: 'Cockpit', icon: LayoutDashboard, axis: 'static' },
  { id: 'master_data', label: 'Stammdaten', icon: ClipboardCheck, axis: 'static' },
  { id: 'accounts', label: 'Kontenrahmen', icon: FileText, axis: 'static' },
  { id: 'inbox', label: 'Posteingang', icon: Inbox, axis: 'dynamic' },
  { id: 'tresor', label: 'Dokumente', icon: FolderOpen, axis: 'dynamic' },
  { id: 'records', label: 'Belege & Rechnungen', icon: Receipt, axis: 'dynamic' },
  { id: 'fixed_assets', label: 'Anlagen / AfA', icon: Boxes, axis: 'functional' },
  { id: 'closures', label: 'Periodenabschlüsse', icon: ClipboardCheck, axis: 'functional' },
  { id: 'voranmeldung', label: 'Voranmeldung', icon: ReceiptText, axis: 'functional' },
  { id: 'audit', label: 'Änderungslog', icon: History, axis: 'audit' },
];

// IA-Reorganisation Phase 3 (Juni 2026): Workflow-orientierte Gruppen.
//   1. Mandant (einmalig einstellen, statisch: Cockpit · Stammdaten · Kontenrahmen · Änderungslog)
//   2. Buchhaltung (Arbeits-Workflow: Belege → Anlagen/AfA → Periodenabschlüsse)
//   3. UVA (nach dem Abschluss, mit Pre-Flight-Check)
//   4. Dokumente (ehem. „Tresor": Dateiaustausch SB ↔ Mandant)
//   5. Kommunikation (Posteingang)
// Die Leaf-IDs (activeTab) bleiben UNVERÄNDERT, damit Year-Selector, PeriodReviewBar,
// Deeplink-Events (ivoliq:advisor-switch-tab / focus-item) weiterhin funktionieren.
const TAB_GROUPS = [
  { id: 'g_client', label: 'Mandant', icon: LayoutDashboard, children: ['overview', 'master_data', 'accounts', 'audit'] },
  { id: 'g_accounting', label: 'Buchhaltung', icon: Receipt, children: ['records', 'fixed_assets', 'closures'] },
  { id: 'g_vat', label: 'UVA', icon: ReceiptText, children: ['voranmeldung'] },
  { id: 'g_documents', label: 'Dokumente', icon: FolderOpen, children: ['tresor'] },
  { id: 'g_communication', label: 'Kommunikation', icon: Inbox, children: ['inbox'] },
];
const LEAF_TO_GROUP = {};
TAB_GROUPS.forEach((g) => g.children.forEach((c) => { LEAF_TO_GROUP[c] = g.id; }));
LEAF_TO_GROUP.mandat = 'g_client'; // Kanzlei-only-Leaf (wird zur Laufzeit eingeblendet)

// Kanzlei-Portal (lex, Sep 2026): reduzierte Navigation — nur das Mandat (Honorarnoten, UVA, ESt-VZ, Audit).
// Einnahmen / Dokumente / Kommunikation sind im Kanzlei-Portal ausgeblendet (User-Entscheid 22.09.2026);
// alle übrigen ivoliq-Funktionen bleiben im Code erhalten.
const LEX_TAB_GROUPS = [
  { id: 'g_client', label: 'Mandat', icon: Scale, children: ['mandat', 'audit'] },
];
const LEX_VISIBLE_LEAVES = LEX_TAB_GROUPS.flatMap((g) => g.children);
const leafMeta = (id) => TABS.find((t) => t.id === id) || { label: id, icon: FileText };
const RECORDS_SUBS = [
  { id: 'invoices', label: 'Einnahmen', icon: FileText, permission: 'invoices' },
  { id: 'expenses', label: 'Ausgaben', icon: Receipt, permission: 'expenses' },
  // Aug 2026 — StB-Reisen-Ansicht (read-only, expenses-Berechtigung)
  { id: 'travel', label: 'Reisen', icon: Plane, permission: 'expenses' },
];

const EXPENSE_CATEGORIES = [
  { key: 'office', label: 'Büromaterial' }, { key: 'software', label: 'Software & Lizenzen' },
  { key: 'travel', label: 'Reisekosten' }, { key: 'hospitality', label: 'Bewirtung' },
  { key: 'vehicle', label: 'Fahrzeug & Fahrkosten' }, { key: 'telecom', label: 'Telefon & Internet' },
  { key: 'insurance', label: 'Versicherungen' }, { key: 'education', label: 'Fortbildung' },
  { key: 'marketing', label: 'Werbung & Marketing' }, { key: 'rent', label: 'Miete & Nebenkosten' },
  { key: 'consulting', label: 'Beratung (StB/Anwalt)' }, { key: 'equipment', label: 'Betriebsausstattung' },
  { key: 'other', label: 'Sonstiges' },
];
const catLabel = (key) => EXPENSE_CATEGORIES.find(c => c.key === key)?.label || key;

const STATUS_OPTIONS = [
  { value: 'created', label: 'Erstellt', css: 'bg-zinc-50 text-zinc-600 border-zinc-200' },
  { value: 'sent', label: 'Gesendet', css: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'paid', label: 'Bezahlt', css: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { value: 'overdue', label: 'Überfällig', css: 'bg-red-50 text-red-700 border-red-200' },
  { value: 'cancelled', label: 'Storniert', css: 'bg-zinc-50 text-zinc-400 border-zinc-200' },
];
const statusCss = (s) => STATUS_OPTIONS.find(o => o.value === s)?.css || 'bg-zinc-50 text-zinc-600 border-zinc-200';
const statusLabel = (s) => STATUS_OPTIONS.find(o => o.value === s)?.label || s;

export default function AdvisorClientDetail() {
  const { clientId } = useParams();
  const location = useLocation();
  // Kanzlei-Modus (lex.ivoliq GmbH, /kanzlei/mandant/:id): Zurück-Ziele auf /kanzlei
  const isKanzlei = location.pathname.startsWith('/kanzlei');
  const backTarget = isKanzlei ? '/kanzlei' : '/advisor/dashboard';
  // Kanzlei-Portal: eigene, reduzierte lex-Navigation
  const tabGroups = isKanzlei ? LEX_TAB_GROUPS : TAB_GROUPS;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(isKanzlei ? 'mandat' : 'overview');
  const [recordsSub, setRecordsSub] = useState('invoices');
  const [clientInfo, setClientInfo] = useState(null);
  const [sectionData, setSectionData] = useState({});
  const [loadingSection, setLoadingSection] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [downloads, setDownloads] = useState([]);
  const [drawerItem, setDrawerItem] = useState(null); // { item, type }
  const [recurringHints, setRecurringHints] = useState([]);
  const [recurringDismissed, setRecurringDismissed] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set()); // Bulk-Prüfung (Juni 2026)
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showLocked, setShowLocked] = useState(() => {
    try { return localStorage.getItem('ivoliq_show_locked') === '1'; } catch { return false; }
  });
  const toggleShowLocked = useCallback(() => {
    setShowLocked(prev => {
      const next = !prev;
      try { localStorage.setItem('ivoliq_show_locked', next ? '1' : '0'); } catch {}
      return next;
    });
  }, []);

  // Help-Overlay (Iter 72): `?` öffnet kontextsensitive Tastatur-Hilfe.
  // Drawer hat sein eigenes Help-Overlay; daher hier deaktiviert wenn Drawer offen.
  const help = useHelpOverlay({ enabled: !drawerItem });

  // Tab-Hotkeys (1–5) — Mandant · Buchhaltung · UVA · Dokumente · Kommunikation
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (drawerItem) return; // Drawer hat eigene Shortcuts
      if (e.key >= '1' && e.key <= '5') {
        const grp = tabGroups[parseInt(e.key, 10) - 1];
        if (grp) {
          e.preventDefault();
          setActiveTab(grp.children[0]);
        }
      } else if (e.key.toLowerCase() === 'g') {
        // Quick: zurück zur Mandantenliste
        e.preventDefault();
        navigate(backTarget);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerItem, navigate, backTarget, tabGroups]);

  useEffect(() => { if (!localStorage.getItem('advisor_authed')) navigate(isKanzlei ? '/kanzlei' : '/steuerberater'); }, [navigate, isKanzlei]);

  // Vier-Augen-Datenzugriff (Sep 2026): Kanzlei-Mitglieder ohne Freischaltung sehen keine Detailansicht
  const [lexAccess, setLexAccess] = useState(true);
  useEffect(() => {
    if (!isKanzlei) return;
    axios.get(`${API}/lex/advisor/my-access`)
      .then((r) => setLexAccess(r.data.data_access !== false))
      .catch(() => {});
  }, [isKanzlei]);

  // Deeplink ?tab=mandat aus dem Kanzlei-Dashboard (Klick auf Mandanten-Zeile)
  useEffect(() => {
    const t = searchParams.get('tab');
    if (!t) return;
    searchParams.delete('tab');
    setSearchParams(searchParams, { replace: true });
    if (t === 'mandat' && !isKanzlei) return;
    if (isKanzlei && !LEX_VISIBLE_LEAVES.includes(t)) return;
    if (TABS.find((x) => x.id === t)) setActiveTab(t);
    // eslint-disable-next-line
  }, []);

  const fetchClientInfo = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/advisor/clients`, { headers: H() });
      const found = (res.data.clients || []).find(c => c.client_id === clientId);
      if (found) setClientInfo(found);
    } catch { /* */ }
  }, [clientId]);

  const fetchSection = useCallback(async (section) => {
    setLoadingSection(true);
    try {
      const res = await axios.get(`${API}/advisor/clients/${clientId}/data?section=${section}&year=${selectedYear}`, { headers: H() });
      setSectionData(prev => ({ ...prev, [`${section}_${selectedYear}`]: res.data.data }));
    } catch (err) {
      if (err.response?.status === 403)
        setSectionData(prev => ({ ...prev, [`${section}_${selectedYear}`]: { _denied: true, message: err.response.data.detail } }));
    } finally { setLoadingSection(false); }
  }, [clientId, selectedYear]);

  // KPI section is part of Records dashboard summary
  const fetchDashboardKpis = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/advisor/clients/${clientId}/data?section=dashboard&year=${selectedYear}`, { headers: H() });
      setSectionData(prev => ({ ...prev, [`dashboard_${selectedYear}`]: res.data.data }));
    } catch { /* */ }
  }, [clientId, selectedYear]);

  const fetchRecurringHints = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/advisor/clients/${clientId}/recurring-hints?year=${selectedYear}`, { headers: H() });
      setRecurringHints(res.data.hints || []);
      setRecurringDismissed(res.data.dismissed || []);
    } catch { setRecurringHints([]); setRecurringDismissed([]); }
  }, [clientId, selectedYear]);

  const fetchDownloads = useCallback(async () => {
    try { const r = await axios.get(`${API}/advisor/clients/${clientId}/downloads`, { headers: H() }); setDownloads(r.data.downloads || []); } catch {}
  }, [clientId]);

  useEffect(() => { fetchClientInfo(); fetchDownloads(); }, [fetchClientInfo, fetchDownloads]);
  useEffect(() => {
    if (activeTab === 'records') {
      fetchSection(recordsSub);
      fetchDashboardKpis();
      fetchRecurringHints();
    } else if (activeTab === 'overview') {
      // Cockpit braucht Finanz-Snapshot + offene Hinweise
      fetchDashboardKpis();
      fetchRecurringHints();
    }
  }, [activeTab, recordsSub, selectedYear, fetchSection, fetchDashboardKpis, fetchRecurringHints]);

  // Listen for deeplinks from PeriodClosureWizard pre-check findings.
  // C9-Cleanup (Feb 2026): Wizard sendet das volle `item`-Objekt im Event-Detail mit,
  // wodurch wir keinen Race auf den nächsten Section-Fetch mehr haben. Für Backward-Compat
  // ist der ID-only-Pfad (sectionData-Lookup + Re-Fetch) erhalten geblieben.
  useEffect(() => {
    const handler = (e) => {
      const { type, id, item } = e.detail || {};
      if (!type || !id) return;
      // Switch to records tab + correct sub-tab
      setActiveTab('records');
      setRecordsSub(type === 'invoice' ? 'invoices' : 'expenses');
      if (item) {
        // Direct payload — open instantly, no race.
        setDrawerItem({ item, type });
        return;
      }
      // Fallback: lookup via cached section, otherwise re-fetch.
      setTimeout(() => {
        const list = sectionData[`${type === 'invoice' ? 'invoices' : 'expenses'}_${selectedYear}`]?.[type === 'invoice' ? 'invoices' : 'expenses'] || [];
        const found = list.find(x => x.id === id);
        if (found) {
          setDrawerItem({ item: found, type });
        } else {
          fetchSection(type === 'invoice' ? 'invoices' : 'expenses').then(() => {
            setTimeout(() => {
              setDrawerItem(prev => prev || { item: { id }, type });
            }, 100);
          });
        }
      }, 200);
    };
    window.addEventListener('ivoliq:focus-item', handler);
    return () => window.removeEventListener('ivoliq:focus-item', handler);
  }, [fetchSection, sectionData, selectedYear]);

  // P1c (Feb 2026) — Closure-Wizard kann an den Voranmeldung-Tab springen
  // (z.B. nach „Entwurf gespeichert" im StepFinalize).
  useEffect(() => {
    const handler = (e) => {
      const t = (e.detail || {}).tab;
      if (isKanzlei && !LEX_VISIBLE_LEAVES.includes(t)) return;
      if (t && TABS.find(x => x.id === t)) setActiveTab(t);
    };
    window.addEventListener('ivoliq:advisor-switch-tab', handler);
    return () => window.removeEventListener('ivoliq:advisor-switch-tab', handler);
  }, []);

  // Deeplink aus neuem Tab: /advisor/client/:id?focus=invoice:ID&year=2026
  // Lädt den Eintrag direkt (eigenständig, ohne Cache-Race) und öffnet den Drawer.
  // Wird von Belegprüfung & Periodenabschluss genutzt (öffnen in neuem Tab).
  useEffect(() => {
    const focus = searchParams.get('focus');
    if (!focus) return;
    const [type, id] = focus.split(':');
    const yr = parseInt(searchParams.get('year'), 10) || selectedYear;
    // Param sofort entfernen, damit ein Reload nicht erneut triggert.
    searchParams.delete('focus');
    searchParams.delete('year');
    setSearchParams(searchParams, { replace: true });
    if ((type !== 'invoice' && type !== 'expense') || !id) return;
    const section = type === 'invoice' ? 'invoices' : 'expenses';
    setSelectedYear(yr);
    setActiveTab('records');
    setRecordsSub(section);
    setShowLocked(true); // gesperrte Einträge ggf. mitanzeigen, damit Deeplink immer trifft
    (async () => {
      try {
        const res = await axios.get(
          `${API}/advisor/clients/${clientId}/data?section=${section}&year=${yr}`,
          { headers: H() }
        );
        setSectionData(prev => ({ ...prev, [`${section}_${yr}`]: res.data.data }));
        const list = res.data?.data?.[section] || [];
        const found = list.find(x => x.id === id);
        if (found) {
          setDrawerItem({ item: found, type });
        } else {
          toast.error('Verknüpfter Eintrag wurde nicht gefunden.');
        }
      } catch {
        toast.error('Eintrag konnte nicht geladen werden.');
      }
    })();
    // eslint-disable-next-line
  }, [searchParams]);

  const downloadedSet = useMemo(() => {
    const s = new Map();
    for (const d of downloads) {
      const key = `${d.entity_type}_${d.entity_id}`;
      if (!s.has(key) || d.created_at > s.get(key)) s.set(key, d.created_at);
    }
    return s;
  }, [downloads]);
  const isDownloaded = (type, id) => downloadedSet.has(`${type}_${id}`);
  const trackDownload = async (entityType, entityId, format = '') => {
    try { await axios.post(`${API}/advisor/clients/${clientId}/track-download`, { entity_type: entityType, entity_id: entityId, format }, { headers: H() }); fetchDownloads(); } catch {}
  };

  const handleReceiptDownload = async (expenseId) => {
    try {
      const res = await axios.get(`${API}/advisor/clients/${clientId}/receipt/${expenseId}`, { headers: H(), responseType: 'blob' });
      window.open(URL.createObjectURL(new Blob([res.data])), '_blank');
      trackDownload('receipt', expenseId);
    } catch { toast.error('Beleg konnte nicht geladen werden'); }
  };

  const handleInvoicePdf = async (invoiceId, invoiceNumber) => {
    try {
      const res = await axios.get(`${API}/advisor/clients/${clientId}/invoice-pdf/${invoiceId}`, { headers: H(), responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      a.download = `Rechnung_${invoiceNumber || invoiceId}.pdf`; a.click();
      trackDownload('invoice', invoiceId, 'pdf');
    } catch { toast.error('PDF konnte nicht geladen werden'); }
  };

  const data = sectionData[`${recordsSub}_${selectedYear}`] || {};
  const dashData = sectionData[`dashboard_${selectedYear}`] || {};
  const permissions = clientInfo?.permissions || {};
  const canEdit = permissions.edit === true;

  const allowedRecordsSubs = RECORDS_SUBS.filter(s => permissions[s.permission] !== false
    && (!isKanzlei || s.id === 'invoices')); // Kanzlei-Portal: nur Einnahmen sichtbar
  useEffect(() => {
    if (activeTab === 'records' && !allowedRecordsSubs.find(s => s.id === recordsSub) && allowedRecordsSubs[0]) {
      setRecordsSub(allowedRecordsSubs[0].id);
    }
  }, [activeTab, recordsSub, allowedRecordsSubs]);

  // Drawer navigation logic
  const currentList = useMemo(() => {
    if (recordsSub === 'invoices') {
      const all = data.invoices || [];
      return showLocked ? all : all.filter(i => !i.is_locked);
    }
    if (recordsSub === 'expenses') {
      const all = data.expenses || [];
      return showLocked ? all : all.filter(e => !e.is_locked);
    }
    return [];
  }, [data, recordsSub, showLocked]);

  const currentIdx = drawerItem ? currentList.findIndex(i => i.id === drawerItem.item.id) : -1;
  const openDrawer = (it) => setDrawerItem({ item: it, type: recordsSub === 'invoices' ? 'invoice' : 'expense' });
  const navigateDrawer = (delta) => {
    if (currentIdx < 0) return;
    const next = currentList[currentIdx + delta];
    if (next) setDrawerItem({ item: next, type: drawerItem.type });
  };
  // Optimistisches Update einer Listenzeile (z.B. Prüf-Markierung) ohne Refetch-Verzögerung.
  const patchListItem = useCallback((itemId, patch) => {
    const key = `${recordsSub}_${selectedYear}`;
    const listKey = recordsSub === 'invoices' ? 'invoices' : 'expenses';
    setSectionData(prev => {
      const section = prev[key];
      if (!section || !Array.isArray(section[listKey])) return prev;
      return {
        ...prev,
        [key]: { ...section, [listKey]: section[listKey].map(it => it.id === itemId ? { ...it, ...patch } : it) },
      };
    });
  }, [recordsSub, selectedYear]);

  const refreshDrawerItem = useCallback(() => {
    fetchSection(recordsSub);
    fetchRecurringHints();
    // Andere SB-Tabs (Belegprüfung / Abschluss) über die Änderung informieren.
    postAdvisorChange({ clientId });
    if (drawerItem) {
      // Refresh the item from latest fetch
      setTimeout(() => {
        const fresh = (sectionData[`${recordsSub}_${selectedYear}`]?.[recordsSub === 'invoices' ? 'invoices' : 'expenses'] || [])
          .find(x => x.id === drawerItem.item.id);
        if (fresh) setDrawerItem(d => ({ ...d, item: fresh }));
      }, 200);
    }
  }, [fetchSection, recordsSub, drawerItem, selectedYear, fetchRecurringHints, clientId]);

  // ──── Bulk-Prüfung (Juni 2026) ────
  // Auswahl wird zurückgesetzt, sobald Sub-Tab oder Jahr wechselt.
  useEffect(() => { setSelectedIds(new Set()); }, [recordsSub, selectedYear]);

  const entityType = recordsSub === 'invoices' ? 'invoice' : 'expense';
  // Auswählbar sind nur nicht-gesperrte, noch nicht geprüfte Positionen.
  const selectableItems = useMemo(
    () => currentList.filter(i => !i.is_locked && !i.reviewed_by_advisor),
    [currentList],
  );
  const reviewedCount = useMemo(
    () => currentList.filter(i => i.reviewed_by_advisor).length,
    [currentList],
  );

  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds(prev => {
      const allIds = selectableItems.map(i => i.id);
      const allSelected = allIds.length > 0 && allIds.every(id => prev.has(id));
      return allSelected ? new Set() : new Set(allIds);
    });
  }, [selectableItems]);

  const handleBulkReview = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const items = Array.from(selectedIds).map(id => ({ type: entityType, id }));
      const res = await axios.post(
        `${API}/advisor/clients/${clientId}/bulk-review`,
        { items, reviewed: true },
        { headers: H() },
      );
      const { updated = 0, skipped_locked = 0 } = res.data || {};
      toast.success(
        `${updated} Position${updated === 1 ? '' : 'en'} als geprüft markiert` +
        (skipped_locked > 0 ? ` · ${skipped_locked} gesperrt übersprungen` : ''),
      );
      setSelectedIds(new Set());
      fetchSection(recordsSub);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler bei der Massenmarkierung');
    } finally {
      setBulkLoading(false);
    }
  }, [selectedIds, entityType, clientId, recordsSub, fetchSection]);

  // Vier-Augen-Prinzip: Kanzlei-Mitglieder ohne Datenzugriff sehen keine Mandanten-Details
  if (isKanzlei && !lexAccess) {
    return (
      <div className="min-h-screen lex-theme" style={{ background: '#FDFCFB' }} data-testid="lex-detail-no-access">
        <div className="max-w-xl mx-auto px-6 py-24 text-center">
          <Lock className="h-10 w-10 mx-auto mb-4" style={{ color: '#C5A059' }} />
          <h1 className="lex-serif text-2xl font-semibold mb-3" style={{ color: '#0A1128' }}>
            Kein Mandantendaten-Zugriff
          </h1>
          <p className="text-sm text-slate-500 mb-6">
            Aus Vertraulichkeitsgründen (Vier-Augen-Prinzip) ist die Detailansicht für Ihr Konto gesperrt.
            Ein Team-Kollege kann Ihren Zugriff im Kanzlei-Portal unter
            <span className="font-semibold"> Sicherheit → Team & Datenzugriff</span> freischalten.
          </p>
          <button onClick={() => navigate('/kanzlei')}
            className="text-xs uppercase tracking-wider font-semibold px-4 py-2"
            style={{ background: '#0A1128', color: '#C5A059' }} data-testid="lex-no-access-back">
            Zurück zum Kanzlei-Portal
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isKanzlei ? 'lex-theme' : 'bg-zinc-50'}`}
      style={isKanzlei ? { background: '#FDFCFB' } : undefined} data-testid="advisor-client-detail">
      <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200/70 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(backTarget)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="advisor-back-btn">
              <ArrowLeft className="w-4 h-4" />Zurück
            </button>
            {isKanzlei && (
              <span className="lex-serif text-xl font-semibold hidden sm:inline" style={{ color: '#0A1128' }} data-testid="lex-detail-wordmark">
                lex<span style={{ color: '#C5A059' }}>.</span>ivoliq
              </span>
            )}
            <div className="h-6 w-px bg-zinc-200" />
            <div>
              <div className={`flex items-center gap-2 ${isKanzlei ? 'lex-serif text-lg font-semibold' : 'text-sm font-semibold text-slate-900'}`}
                style={isKanzlei ? { color: '#0A1128' } : undefined}>
                {clientInfo?.name || '...'}
                <CountryFeatureBadge mode="client" clientId={clientId} />
              </div>
              <div className={`text-xs text-slate-500 ${isKanzlei ? 'lex-mono' : ''}`}>{clientInfo?.company_name || clientInfo?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canEdit && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 font-medium uppercase tracking-wider">Vollmacht</span>
            )}
            {/* C6-Cleanup (Feb 2026): Year + showLocked nur dort anzeigen, wo sie wirken
                (Belege + Periodenabschlüsse) — in Dokumente / Posteingang / Audit nicht. */}
            {(activeTab === 'records' || activeTab === 'closures') && (
              <>
                <div className="flex items-center gap-1 border border-slate-200 bg-white rounded-lg" data-testid="year-selector">
                  <button onClick={() => setSelectedYear(y => y - 1)} className="px-2 py-1.5 hover:bg-slate-50 rounded-l-lg"><ChevronLeft className="w-3 h-3" /></button>
                  <span className="text-sm font-mono font-medium text-slate-900 px-2">{selectedYear}</span>
                  <button onClick={() => setSelectedYear(y => Math.min(y + 1, new Date().getFullYear()))} className="px-2 py-1.5 hover:bg-slate-50 rounded-r-lg" disabled={selectedYear >= new Date().getFullYear()}>
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
                <button
                  onClick={toggleShowLocked}
                  title={showLocked ? 'Festgeschriebene Perioden ausblenden' : 'Festgeschriebene Perioden anzeigen'}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs border rounded-lg transition-colors ${
                    showLocked ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                  }`}
                  data-testid="toggle-show-locked-btn">
                  {showLocked ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                  Festgeschr. {showLocked ? 'an' : 'aus'}
                </button>
                <div className="h-6 w-px bg-slate-200 mx-1" />
              </>
            )}
            <Button onClick={() => setShowExportDialog(true)}
              className={isKanzlei
                ? 'rounded-none text-xs h-8 uppercase tracking-wider hover:opacity-90'
                : 'bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs h-8 shadow-sm'}
              style={isKanzlei ? { background: '#0A1128', color: '#C5A059' } : undefined}
              data-testid="open-export-dialog-btn">
              <Download className="w-3 h-3 mr-1.5" />Export
            </Button>
            <HelpButton onClick={help.toggle} testId="advisor-clientdetail-help-btn" />
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Sprint-0 (Feb 2026): Country-Feature-Banner — nur sichtbar bei Ländern
            mit eingeschränktem Funktionsumfang (z.B. CH). */}
        <CountryFeatureBadge mode="client" clientId={clientId} variant="banner" className="mb-4" />

        {/* Top-level group tabs (Phase 3 Juni 2026 — Workflow-orientierte Gruppen) */}
        <div className={`flex gap-1 border border-slate-200 bg-white mb-3 overflow-x-auto p-1 ${isKanzlei ? 'rounded-none' : 'rounded-xl'}`} data-testid="client-detail-tabs">
          {tabGroups.map((g) => {
            const isActive = LEAF_TO_GROUP[activeTab] === g.id;
            const GIcon = g.icon;
            return (
              <button key={g.id} onClick={() => setActiveTab(g.children[0])}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${
                  isKanzlei ? 'rounded-none uppercase tracking-wider text-xs font-semibold' : 'rounded-lg'} ${
                  isActive
                    ? (isKanzlei ? '' : 'bg-emerald-600 text-white shadow-sm')
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}
                style={isActive && isKanzlei ? { background: '#0A1128', color: '#C5A059' } : undefined}
                data-testid={`tab-group-${g.id}`}>
                <GIcon className="w-4 h-4" strokeWidth={1.5} />{g.label}
              </button>
            );
          })}
        </div>

        {/* Leaf sub-tabs within active group (nur bei >1 Leaf) */}
        {(() => {
          const g = tabGroups.find((x) => x.id === LEAF_TO_GROUP[activeTab]);
          if (!g || g.children.length <= 1) return null;
          return (
            <div className="flex gap-1.5 mb-4 overflow-x-auto" data-testid="client-detail-subtabs">
              {g.children.map((leaf) => {
                const m = leafMeta(leaf);
                const LIcon = m.icon;
                return (
                  <button key={leaf} onClick={() => setActiveTab(leaf)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border transition-colors whitespace-nowrap ${
                      isKanzlei ? 'rounded-none uppercase tracking-wider' : 'rounded-full'} ${
                      activeTab === leaf
                        ? (isKanzlei ? 'border-[#C5A059] bg-[#FBF7EF] text-[#92650B]' : 'border-emerald-200 bg-emerald-50 text-emerald-700')
                        : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'}`}
                    data-testid={`tab-${leaf}`}>
                    <LIcon className="w-3.5 h-3.5" strokeWidth={1.5} />{m.label}
                  </button>
                );
              })}
            </div>
          );
        })()}

        {/* Sub-tabs for Records */}
        {activeTab === 'records' && allowedRecordsSubs.length > 1 && (
          <SubTabs subs={allowedRecordsSubs} value={recordsSub} onChange={setRecordsSub} testIdPrefix="records-sub" />
        )}

        {/* Content */}
        {(activeTab === 'mandat') ? (
          <LexMandatTab clientId={clientId} />
        ) : (activeTab === 'overview') ? (
          <ClientCockpit
            clientInfo={clientInfo}
            clientId={clientId}
            data={dashData}
            year={selectedYear}
            recurringHintsCount={recurringHints.length}
            onGoto={setActiveTab}
            onExport={() => setShowExportDialog(true)}
          />
        ) : (activeTab === 'tresor') ? (
          <Tresor mode="advisor" clientId={clientId} />
        ) : (activeTab === 'inbox') ? (
          <CollabInbox mode="advisor" clientId={clientId} initialFilter="mine" />
        ) : (activeTab === 'audit') ? (
          <AuditLogTab clientId={clientId} />
        ) : (activeTab === 'master_data') ? (
          <MasterDataTab clientId={clientId} canEdit={canEdit} />
        ) : (activeTab === 'accounts') ? (
          <AccountMappingTab clientId={clientId} />
        ) : (activeTab === 'voranmeldung') ? (
          <AdvisorVoranmeldungTab clientId={clientId} canEdit={canEdit} clientCountry={clientInfo?.country} />
        ) : (activeTab === 'closures') ? (
          /* Periodenabschlüsse (Juni 2026): Belegprüfung ZUERST (Vorprüfung),
             danach der Periodenabschluss-Wizard. */
          <div className="space-y-4">
            <PeriodCheckPanel clientId={clientId} year={selectedYear} canEdit={canEdit} />
            <PeriodReviewBar clientId={clientId} year={selectedYear} onYearChange={setSelectedYear} canEdit={canEdit} />
          </div>
        ) : (activeTab === 'fixed_assets') ? (
          <AdvisorFixedAssetsTab clientId={clientId} canEdit={canEdit} />
        ) : loadingSection ? (
          <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-sm text-slate-500">Laden...</div>
        ) : data._denied ? (
          <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center">
            <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-3" /><p className="text-sm text-slate-600">{data.message}</p>
          </div>
        ) : (
          <>
            {/* KPI Mini-Card on top of records */}
            <KpiMiniCard data={dashData} year={selectedYear} />

            {/* Recurring Hints Banner — nur einblenden, wenn es vermutete fehlende
                wiederkehrende Rechnungen gibt (Anzahl > 0). Bei 0 (auch wenn nur
                abgelehnte existieren) ergibt der Hinweis keinen Sinn. */}
            {!isKanzlei && recurringHints.length > 0 && (
              <RecurringHintsBanner hints={recurringHints} dismissed={recurringDismissed} clientId={clientId} onChanged={fetchRecurringHints} />
            )}

            {recordsSub === 'invoices' && (
              <>
                <ReviewProgress reviewed={reviewedCount} total={currentList.length} />
                <InvoicesSection data={data} onPdf={handleInvoicePdf} isDownloaded={isDownloaded}
                  canEdit={canEdit} onOpen={openDrawer} showLocked={showLocked}
                  selectedIds={selectedIds} onToggleSelect={toggleSelect}
                  onToggleSelectAll={toggleSelectAll} selectableCount={selectableItems.length}
                  onToggleIncomeReview={async (incomeId) => {
                    try {
                      await axios.post(`${API}/advisor/clients/${clientId}/income/${incomeId}/review-toggle`, {}, { headers: H() });
                      fetchSection('invoices');
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Fehler beim Markieren');
                    }
                  }} />
              </>
            )}
            {recordsSub === 'expenses' && (
              <>
                <ReviewProgress reviewed={reviewedCount} total={currentList.length} />
                <ExpensesSection data={data} onReceipt={handleReceiptDownload} isDownloaded={isDownloaded}
                  canEdit={canEdit} clientId={clientId} onOpen={openDrawer} showLocked={showLocked}
                  selectedIds={selectedIds} onToggleSelect={toggleSelect}
                  onToggleSelectAll={toggleSelectAll} selectableCount={selectableItems.length} />
              </>
            )}
            {recordsSub === 'travel' && (
              <AdvisorTravelSection data={data} />
            )}
          </>
        )}
      </div>

      {showExportDialog && (
        <ExportDialog clientId={clientId} clientName={clientInfo?.name} clientCountry={clientInfo?.country} year={selectedYear}
          onClose={() => setShowExportDialog(false)}
          onTrack={trackDownload}
          onOpenExpense={(expenseId) => {
            // Feb 2026 — Compliance-Issue Tap-to-Fix: schließe Export-Dialog,
            // nutze den existing `ivoliq:focus-item`-Event-Pfad (robuster gegen Closure-Races)
            setShowExportDialog(false);
            window.dispatchEvent(new CustomEvent('ivoliq:focus-item', {
              detail: { id: expenseId, type: 'expense' },
            }));
          }}
        />
      )}

      {drawerItem && (
        <ItemDetailDrawer
          open={!!drawerItem}
          onClose={() => setDrawerItem(null)}
          item={drawerItem.item}
          itemType={drawerItem.type}
          clientId={clientId}
          canEdit={canEdit}
          clientCountry={clientInfo?.country || 'DE'}
          clientIsPro={['pro', 'pro_monthly', 'pro_yearly', 'lifetime'].includes(clientInfo?.plan)}
          onChanged={refreshDrawerItem}
          onItemPatched={patchListItem}
          hasPrev={currentIdx > 0}
          hasNext={currentIdx >= 0 && currentIdx < currentList.length - 1}
          onPrev={() => navigateDrawer(-1)}
          onNext={() => navigateDrawer(1)}
        />
      )}

      <AdvisorFeedbackButton />

      {/* Bulk-Prüfung Floating Action Bar (Juni 2026) — nur im Records-Tab,
          versteckt wenn der Drawer offen ist (sonst überlappend). */}
      {activeTab === 'records' && !drawerItem && (
        <BulkActionBar
          count={selectedIds.size}
          loading={bulkLoading}
          onVerify={handleBulkReview}
          onCancel={() => setSelectedIds(new Set())}
        />
      )}

      <HelpOverlay
        isOpen={help.isOpen}
        onClose={help.close}
        title="Mandanten-Ansicht — Tastatur-Shortcuts"
        sections={[
          {
            title: 'Tab-Navigation',
            shortcuts: [
              { keys: ['1'], label: 'Mandant (Cockpit)' },
              { keys: ['2'], label: 'Buchhaltung (Belege · AfA · Abschlüsse)' },
              { keys: ['3'], label: 'UVA / Voranmeldung' },
              { keys: ['4'], label: 'Dokumente' },
              { keys: ['5'], label: 'Kommunikation' },
            ],
          },
          {
            title: 'Allgemein',
            shortcuts: [
              { keys: ['?'], label: 'Diese Hilfe öffnen / schließen' },
              { keys: ['G'], label: 'Zurück zur Mandantenliste' },
              { keys: ['Klick', 'auf Beleg'], label: 'Detail-Drawer öffnen (eigene Shortcuts)' },
            ],
          },
          {
            title: 'Detail-Drawer',
            shortcuts: [
              { keys: ['j', '↓'], label: 'Nächster Beleg' },
              { keys: ['k', '↑'], label: 'Vorheriger Beleg' },
              { keys: ['r'], label: 'Als geprüft markieren' },
              { keys: ['e'], label: 'Bearbeiten-Modus (mit Vollmacht)' },
              { keys: ['Esc'], label: 'Drawer schließen' },
            ],
          },
        ]}
      />
    </div>
  );
}

// ──────── Mandanten-Cockpit (Phase 2b — Übersicht statt direkt in Tabs) ────────

const _USTVA_CLR = {
  ready: { cls: 'border-emerald-200 bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500' },
  open: { cls: 'border-amber-200 bg-amber-50 text-amber-700', dot: 'bg-amber-500' },
  overdue: { cls: 'border-rose-200 bg-rose-50 text-rose-700', dot: 'bg-rose-500' },
  none: { cls: 'border-slate-200 bg-slate-50 text-slate-500', dot: 'bg-slate-300' },
};

function CockpitCard({ icon: Icon, title, accent = 'text-slate-400', children, testId }) {
  return (
    <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-5 sm:p-6 flex flex-col" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className={`w-4 h-4 ${accent}`} strokeWidth={1.75} />
        <h3 className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function CockpitRow({ label, value, valueClass = 'text-slate-900' }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className={`text-sm font-medium ${valueClass}`}>{value}</span>
    </div>
  );
}

function ClientCockpit({ clientInfo, clientId, data, year, recurringHintsCount, onGoto, onExport }) {
  const ci = clientInfo || {};
  const status = ci.ustva_status || 'none';
  const m = _USTVA_CLR[status] || _USTVA_CLR.none;
  const days = ci.days_until_deadline;
  const profit = (data && data.profit) || 0;
  return (
    <div className="space-y-4">
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="advisor-client-cockpit">
      {/* Stammdaten */}
      <CockpitCard icon={Building2} title="Stammdaten" testId="cockpit-master-data">
        <div className="space-y-0.5">
          <CockpitRow label="Mandant" value={ci.name || '—'} />
          <CockpitRow label="Firma" value={ci.company_name || '—'} />
          <CockpitRow label="Land" value={ci.country || '—'} />
          <CockpitRow label="Plan" value={(ci.plan || '—').toString().toUpperCase()} />
          <CockpitRow label="Vollmacht" value={ci.permissions?.edit ? 'Ja' : 'Nein'}
            valueClass={ci.permissions?.edit ? 'text-amber-700' : 'text-slate-500'} />
          <CockpitRow label="Verbunden seit" value={ci.connected_since ? fmtDate(ci.connected_since) : '—'} />
        </div>
        <button onClick={() => onGoto('master_data')} className="mt-4 text-xs font-medium text-emerald-700 hover:underline self-start" data-testid="cockpit-goto-master-data">
          Stammdaten öffnen →
        </button>
      </CockpitCard>

      {/* Finanzen */}
      <CockpitCard icon={Wallet} title={`Finanzen ${year}`} testId="cockpit-finance">
        {data && (data.total_revenue !== undefined && data.total_revenue !== null) ? (
          <div className="space-y-0.5">
            <CockpitRow label="Einnahmen" value={`+${fmtCur(data.total_revenue)}`} valueClass="text-emerald-700" />
            <CockpitRow label="Ausgaben" value={`−${fmtCur(data.total_expenses)}`} valueClass="text-rose-700" />
            <CockpitRow label="Gewinn netto" value={fmtCur(profit)} valueClass={profit >= 0 ? 'text-emerald-700' : 'text-rose-700'} />
            <CockpitRow label="USt-Saldo" value={fmtCur(data.vat_balance)} valueClass="text-blue-700" />
          </div>
        ) : (
          <p className="text-sm text-slate-400">Keine Finanzdaten für {year}.</p>
        )}
        <button onClick={() => onGoto('records')} className="mt-4 text-xs font-medium text-emerald-700 hover:underline self-start" data-testid="cockpit-goto-records">
          Belege & Rechnungen →
        </button>
      </CockpitCard>

      {/* Status & Fristen */}
      <CockpitCard icon={CalendarClock} title="Status & Fristen" testId="cockpit-status">
        <div className="space-y-3">
          <div>
            <div className="text-sm text-slate-500 mb-1.5">UStVA-Status</div>
            <span className={`inline-flex w-fit items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${m.cls}`} data-testid="cockpit-ustva-badge">
              <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
              {ci.ustva_status_label || 'Unbekannt'}
            </span>
          </div>
          {ci.next_deadline ? (
            <CockpitRow label="Nächste Frist"
              value={`${ci.next_deadline_period} · ${fmtDate(ci.next_deadline)}`}
              valueClass={status === 'overdue' ? 'text-rose-600' : 'text-slate-900'} />
          ) : (
            ci.ustva_reason && <CockpitRow label="Hinweis" value={ci.ustva_reason} valueClass="text-slate-400" />
          )}
          {ci.next_deadline && typeof days === 'number' && status !== 'ready' && (
            <CockpitRow label="Verbleibend"
              value={days < 0 ? `${Math.abs(days)} Tage überfällig` : `in ${days} Tagen`}
              valueClass={days < 0 ? 'text-rose-600' : 'text-slate-700'} />
          )}
          <CockpitRow label="Offene Hinweise" value={recurringHintsCount > 0 ? `${recurringHintsCount}` : '—'}
            valueClass={recurringHintsCount > 0 ? 'text-amber-700' : 'text-slate-400'} />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={() => onGoto('voranmeldung')} className="text-xs font-medium text-emerald-700 hover:underline" data-testid="cockpit-goto-voranmeldung">Voranmeldung →</button>
          <span className="text-slate-200">·</span>
          <button onClick={() => onGoto('inbox')} className="text-xs font-medium text-emerald-700 hover:underline" data-testid="cockpit-goto-inbox">Posteingang →</button>
          <span className="text-slate-200">·</span>
          <button onClick={onExport} className="text-xs font-medium text-emerald-700 hover:underline" data-testid="cockpit-goto-export">Export →</button>
        </div>
      </CockpitCard>
    </div>
    {/* Investitionsfreibetrag (AT) — Steuer-Optimierung, self-gating */}
    <AdvisorInvestmentAllowanceControl clientId={clientId} year={year} />
    </div>
  );
}

// ──────── KPI Mini-Card (replaces "Übersicht" tab) ────────

function KpiMiniCard({ data, year }) {
  if (!data || (data.total_revenue === undefined && data.total_revenue === null)) return null;
  const profit = data.profit || 0;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="advisor-kpi-mini">
      <KpiTile label={`Einnahmen ${year}`} value={fmtCur(data.total_revenue)} accent="border-t-emerald-500" valueClass="text-emerald-700" prefix="+" />
      <KpiTile label={`Ausgaben ${year}`} value={fmtCur(data.total_expenses)} accent="border-t-rose-500" valueClass="text-rose-700" prefix="−" />
      <KpiTile label="Gewinn netto" value={fmtCur(profit)} accent={profit >= 0 ? 'border-t-emerald-500' : 'border-t-rose-500'} valueClass={profit >= 0 ? 'text-emerald-700' : 'text-rose-700'} />
      <KpiTile label="USt-Saldo" value={fmtCur(data.vat_balance)} accent="border-t-blue-500" valueClass="text-blue-700" />
    </div>
  );
}

function KpiTile({ label, value, accent, valueClass, prefix }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-lg p-4 shadow-none transition-shadow hover:shadow-md`}>
      <div className="text-[11px] text-slate-400 uppercase tracking-wider font-semibold mb-1">{label}</div>
      <div className={`text-lg font-bold ${valueClass || 'text-slate-900'} tabular-nums`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
        {prefix}{value}
      </div>
    </div>
  );
}

// ──────── Recurring Hints Banner ────────

function RecurringHintsBanner({ hints, dismissed = [], clientId, onChanged }) {
  const [collapsed, setCollapsed] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);
  const [rejectFor, setRejectFor] = useState(null); // hint being rejected
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const grouped = hints.slice(0, 8);

  const submitReject = async () => {
    if (!reason.trim()) { toast.error('Bitte eine Begründung angeben.'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/advisor/clients/${clientId}/recurring-hints/dismiss`,
        { vendor_key: rejectFor.vendor_key, missing_period: rejectFor.missing_period, reason: reason.trim() },
        { headers: H() });
      toast.success('Vermutung abgelehnt');
      setRejectFor(null); setReason('');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ablehnen fehlgeschlagen');
    } finally { setBusy(false); }
  };

  const restore = async (h) => {
    setBusy(true);
    try {
      await axios.post(`${API}/advisor/clients/${clientId}/recurring-hints/restore`,
        { vendor_key: h.vendor_key, missing_period: h.missing_period }, { headers: H() });
      toast.success('Vermutung wiederhergestellt');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aktion fehlgeschlagen');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl mb-4 overflow-hidden" data-testid="recurring-hints-banner">
      <button onClick={() => setCollapsed(c => !c)} className="w-full px-4 py-2.5 flex items-center justify-between text-left">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-amber-700" />
          <div className="text-sm font-medium text-amber-900">
            {hints.length} vermutet fehlende, wiederkehrende Rechnung{hints.length === 1 ? '' : 'en'}
          </div>
        </div>
        <span className="text-xs text-amber-800">{collapsed ? 'Anzeigen' : 'Ausblenden'}</span>
      </button>
      {!collapsed && (
        <div className="border-t border-amber-200 px-4 py-2 space-y-1.5">
          {grouped.map(h => (
            <div key={h.id} className="text-xs text-amber-900 flex items-center justify-between gap-3" data-testid={`recurring-hint-${h.vendor_key}-${h.missing_period}`}>
              <span className="min-w-0">
                <strong>{h.vendor_name}</strong> — fehlt für <strong>{h.missing_period_label}</strong>
                <span className="text-amber-700"> · ~{fmt(h.expected_amount)} € · {h.occurrences_last_6m}× in letzten 6 Monaten</span>
                {h.last_seen_label && <span className="text-amber-700"> · zuletzt: {h.last_seen_label}</span>}
              </span>
              <button
                onClick={() => { setRejectFor(h); setReason(''); }}
                disabled={busy}
                className="shrink-0 text-[11px] font-medium text-amber-800 hover:text-amber-950 underline underline-offset-2 disabled:opacity-50"
                data-testid={`reject-hint-${h.vendor_key}-${h.missing_period}`}
              >
                Ablehnen
              </button>
            </div>
          ))}
          {hints.length > grouped.length && (
            <div className="text-[11px] text-amber-700 italic pt-1">… und {hints.length - grouped.length} weitere.</div>
          )}
          {dismissed.length > 0 && (
            <div className="pt-2 mt-1 border-t border-amber-200">
              <button onClick={() => setShowDismissed(s => !s)} className="text-[11px] text-amber-700 hover:text-amber-900" data-testid="toggle-dismissed-hints">
                {showDismissed ? 'Abgelehnte ausblenden' : `${dismissed.length} abgelehnte anzeigen`}
              </button>
              {showDismissed && (
                <div className="mt-1 space-y-1">
                  {dismissed.map(h => (
                    <div key={h.id} className="text-[11px] text-amber-800/80 flex items-center justify-between gap-3">
                      <span className="min-w-0 line-through decoration-amber-400">
                        <strong>{h.vendor_name}</strong> · {h.missing_period_label}
                        {h.dismiss_reason && <span className="no-underline"> — Begründung: „{h.dismiss_reason}"</span>}
                        {h.dismissed_by && <span className="no-underline text-amber-600"> ({h.dismissed_by})</span>}
                      </span>
                      <button onClick={() => restore(h)} disabled={busy}
                        className="shrink-0 text-[11px] text-amber-700 hover:text-amber-950 underline underline-offset-2 disabled:opacity-50"
                        data-testid={`restore-hint-${h.vendor_key}-${h.missing_period}`}>
                        Wiederherstellen
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {rejectFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="reject-hint-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-1">Vermutung ablehnen</h3>
            <p className="text-xs text-slate-600 mb-3">
              <strong>{rejectFor.vendor_name}</strong> — fehlt für <strong>{rejectFor.missing_period_label}</strong>.
              Bitte begründe, warum hier keine wiederkehrende Rechnung fehlt (z. B. „Vertrag gekündigt", „Zahlungspause", „einmalig").
            </p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              autoFocus
              placeholder="Begründung …"
              className="w-full text-sm border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
              data-testid="reject-hint-reason"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => { setRejectFor(null); setReason(''); }} disabled={busy}
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                data-testid="reject-hint-cancel">
                Abbrechen
              </button>
              <button onClick={submitReject} disabled={busy || !reason.trim()}
                className="px-3 py-1.5 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                data-testid="reject-hint-confirm">
                {busy ? 'Speichern …' : 'Ablehnen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ──────── Invoices Section (color-coded, paid_at column, sticky sums) ────────

function InvoicesSection({ data, onPdf, isDownloaded, canEdit, onOpen, showLocked, onToggleIncomeReview, selectedIds, onToggleSelect, onToggleSelectAll, selectableCount = 0 }) {
  const allInvoices = data?.invoices || [];
  const invoices = showLocked ? allInvoices : allInvoices.filter(i => !i.is_locked);
  const hiddenCount = allInvoices.length - invoices.length;

  // Sums — Aug 2026 Fix: IMMER über ALLE Rechnungen des Jahres (allInvoices),
  // damit ausgeblendete festgeschriebene Perioden die Summen nicht verfälschen.
  // Feb 2026 — Multi-VAT-Fix: nutzt getInvoiceVatTotal (zentraler Helper),
  // damit Multi-Tax-Rechnungen mit items[] verschiedener Sätze korrekt aggregieren.
  // Feb 2026 — Storno-Aufschlüsselung: positiv und negativ getrennt darstellen.
  const sums = useMemo(() => {
    const base = allInvoices;
    const positives = base.filter(i => (i.total || 0) >= 0 && (i.invoice_type || 'invoice') !== 'storno');
    const stornos = base.filter(i => (i.invoice_type || '') === 'storno' || (i.total || 0) < 0);
    const cancelled = base.filter(i => i.status === 'cancelled');
    const brutto = base.reduce((s, i) => s + (i.total || 0), 0);
    const netto = base.reduce((s, i) => s + (i.subtotal || i.total || 0), 0);
    const ust = base.reduce((s, i) => s + getInvoiceVatTotal(i), 0);
    const positiveBrutto = positives.reduce((s, i) => s + (i.total || 0), 0);
    const stornoBrutto = stornos.reduce((s, i) => s + (i.total || 0), 0); // bereits negativ
    // Offen = fakturiert, aber noch nicht zugeflossen (kein Storno, nicht aufgehoben, nicht bezahlt)
    const openBrutto = base
      .filter(i => !['paid', 'cancelled'].includes(i.status) && (i.invoice_type || 'invoice') !== 'storno' && (i.total || 0) >= 0)
      .reduce((s, i) => s + (i.total || 0), 0);
    return { brutto, netto, ust, positiveBrutto, stornoBrutto, openBrutto, cancelledCount: cancelled.length, stornoCount: stornos.length };
  }, [allInvoices]);

  if (!invoices.length && !(data?.other_income?.length)) {
    if (hiddenCount > 0) {
      return <Empty text={`Alle ${hiddenCount} Rechnung(en) liegen in festgeschriebenen Perioden und sind ausgeblendet.`} />;
    }
    return <Empty text="Keine Rechnungen oder Einnahmen in diesem Zeitraum." />;
  }

  return (
    <>
      {hiddenCount > 0 && (
        <div className="mb-3 text-xs text-slate-500 flex items-center gap-1.5" data-testid="invoices-locked-hint">
          <Lock className="w-3 h-3" />{hiddenCount} Rechnung(en) in festgeschriebenen Perioden ausgeblendet
        </div>
      )}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden" data-testid="section-invoices">
        <table className="w-full">
          <thead><tr className="border-b border-zinc-200 bg-zinc-50">
            <th className="text-center px-3 py-3 w-8">
              <Checkbox
                checked={selectableCount > 0 && selectedIds?.size === selectableCount}
                onCheckedChange={onToggleSelectAll}
                disabled={selectableCount === 0}
                aria-label="Alle auswählen"
                data-testid="bulk-select-all-invoices"
              />
            </th>
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3 w-8"></th>
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3">Nr.</th>
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3">Kunde</th>
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3 hidden md:table-cell">Datum</th>
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3 hidden lg:table-cell">Bezahlt</th>
            <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3">Brutto</th>
            <th className="text-center text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3">Status</th>
            <th className="text-center text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3 w-20">Aktionen</th>
          </tr></thead>
          <tbody>
            {invoices.map(inv => {
              const reviewed = !!inv.reviewed_by_advisor;
              const flagged = (inv.open_thread_count || 0) > 0;
              const invType = inv.invoice_type || 'invoice';
              const isStorno = invType === 'storno';
              const isCorrection = invType === 'correction';
              const isCancelled = inv.status === 'cancelled';
              const amountColor = isStorno || (inv.total || 0) < 0
                ? 'text-rose-700'
                : isCancelled
                  ? 'text-slate-400 line-through'
                  : 'text-emerald-700';
              const amountSign = (inv.total || 0) < 0 ? '' : '+'; // negative bringen ihr Minus selbst mit
              return (
                <tr key={inv.id} onClick={() => onOpen(inv)}
                  className={`border-b border-zinc-100 hover:bg-emerald-50/30 cursor-pointer transition-colors ${inv.is_locked ? 'opacity-60' : ''} ${isCancelled ? 'bg-slate-50/60' : ''}`}
                  data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-3 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                    {!inv.is_locked && !reviewed ? (
                      <Checkbox
                        checked={selectedIds?.has(inv.id) || false}
                        onCheckedChange={() => onToggleSelect?.(inv.id)}
                        aria-label="Position auswählen"
                        data-testid={`bulk-select-row-${inv.id}`}
                      />
                    ) : null}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <ReviewMarker reviewed={reviewed} flagged={flagged} />
                  </td>
                  <td className="px-3 py-2.5 text-sm font-mono text-slate-900">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {inv.is_locked && <Lock className="w-3 h-3 text-slate-400" title={`Festgeschrieben: ${inv.locked_period_label || ''}`} />}
                      <span className={isCancelled ? 'line-through text-slate-400' : ''}>{inv.invoice_number || '—'}</span>
                      {isStorno && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-200 font-sans uppercase tracking-wide" data-testid={`sb-badge-storno-${inv.id}`}>
                          Storno
                        </span>
                      )}
                      {isCorrection && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 font-sans uppercase tracking-wide" data-testid={`sb-badge-correction-${inv.id}`}>
                          Korrektur
                        </span>
                      )}
                      {isCancelled && !isStorno && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 font-sans uppercase tracking-wide" data-testid={`sb-badge-cancelled-${inv.id}`}>
                          Aufgehoben
                        </span>
                      )}
                    </div>
                    {(isStorno || isCancelled || isCorrection) && (inv.correction_reason || inv.cancels_invoice_id || inv.cancelled_by_invoice_id || inv.correction_replaces_invoice_id) && (
                      <div className="text-[11px] text-slate-500 font-sans mt-0.5 italic max-w-[220px] truncate" title={inv.correction_reason || ''}>
                        {isStorno && 'storniert Original'}
                        {isCorrection && 'ersetzt frühere Rechnung'}
                        {isCancelled && !isStorno && 'durch Storno aufgehoben'}
                        {inv.correction_reason ? ` · ${inv.correction_reason}` : ''}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-sm text-slate-700 truncate max-w-[180px]">{inv.customer_name || '—'}</td>
                  <td className="px-3 py-2.5 text-sm text-slate-500 hidden md:table-cell">{fmtDate(inv.invoice_date || inv.date)}</td>
                  <td className="px-3 py-2.5 text-sm hidden lg:table-cell">
                    {inv.paid_at
                      ? <span className="text-emerald-700 font-medium">{fmtDate(inv.paid_at)}</span>
                      : <span className="text-slate-300">—</span>}
                  </td>
                  <td className={`px-3 py-2.5 text-sm text-right font-medium tabular-nums ${amountColor}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {amountSign}{fmt(inv.total)} €
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={`text-xs px-2 py-0.5 border rounded ${statusCss(inv.status)}`}>
                      {statusLabel(inv.status)}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-1.5">
                      <button onClick={() => onPdf(inv.id, inv.invoice_number)}
                        className="text-slate-400 hover:text-slate-900 transition-colors" title="PDF" data-testid={`pdf-${inv.id}`}>
                        <FileDown className="w-4 h-4" />
                      </button>
                      {flagged && <MessageSquare className="w-3.5 h-3.5 text-blue-600" />}
                      {isDownloaded('invoice', inv.id) && <Check className="w-3.5 h-3.5 text-emerald-500" />}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {/* Sticky Footer with Sums */}
          <tfoot>
            <tr className="bg-emerald-50 border-t-2 border-emerald-200 font-medium">
              <td colSpan="6" className="px-3 py-2.5 text-xs text-slate-700">
                Σ {allInvoices.length} Rechnung{allInvoices.length === 1 ? '' : 'en'} (alle Status{hiddenCount > 0 ? ', inkl. festgeschriebener' : ''}) · Netto {fmt(sums.netto)} € · USt {fmt(sums.ust)} €
                {sums.stornoCount > 0 && (
                  <span className="ml-2 text-rose-700">
                    · davon {sums.stornoCount} Storno ({fmt(sums.stornoBrutto)} €)
                  </span>
                )}
                {sums.cancelledCount > 0 && (
                  <span className="ml-2 text-slate-500">
                    · {sums.cancelledCount} aufgehoben
                  </span>
                )}
              </td>
              <td className="px-3 py-2.5 text-sm text-right font-bold tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                <span className={sums.brutto >= 0 ? 'text-emerald-700' : 'text-rose-700'}>
                  {sums.brutto >= 0 ? '+' : ''}{fmt(sums.brutto)} €
                </span>
              </td>
              <td colSpan="2"></td>
            </tr>
            {/* Aug 2026 — Zufluss-Zeile: identische Basis wie Finanztab (db.income),
                damit SB-Portal und Mandanten-Finanzen dieselben Einnahmen zeigen. */}
            <tr className="bg-white border-t border-emerald-100 text-xs" data-testid="invoices-cashflow-row">
              <td colSpan="6" className="px-3 py-2 text-slate-600">
                Zugeflossene Einnahmen {'\u2261'} Finanzen-Tab des Mandanten (bezahlt, inkl. sonstiger Einnahmen)
                {sums.openBrutto > 0.005 && (
                  <span className="ml-2 text-amber-700">· noch offen (fakturiert, unbezahlt): {fmt(sums.openBrutto)} €</span>
                )}
              </td>
              <td className="px-3 py-2 text-right font-bold tabular-nums text-emerald-700" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="invoices-income-total">
                {typeof data?.income_total_year === 'number' ? `${data.income_total_year >= 0 ? '+' : ''}${fmt(data.income_total_year)} €` : '–'}
              </td>
              <td colSpan="2"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Other Income */}
      {(data?.other_income || []).length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden mt-4" data-testid="section-other-income">
          <div className="px-4 py-2.5 border-b border-zinc-200 bg-zinc-50">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Sonstige Einnahmen ({data.other_income.length})</span>
          </div>
          <table className="w-full">
            <tbody>
              {data.other_income.map((inc, idx) => (
                <tr key={inc.id || idx} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50/50" data-testid={`other-income-${inc.id || idx}`}>
                  <td className="px-3 py-2.5 w-10 text-center">
                    {/* C11-Cleanup: Manuelle Einnahmen wie reguläre Rechnungen mit Geprüft-Toggle */}
                    <button
                      onClick={() => inc.id && onToggleIncomeReview?.(inc.id)}
                      disabled={!inc.id || inc.is_locked}
                      title={inc.is_locked ? 'Periode gesperrt' : (inc.reviewed_by_advisor ? 'Prüf-Markierung zurücknehmen' : 'Als geprüft markieren')}
                      className="hover:scale-110 transition-transform disabled:opacity-40 disabled:hover:scale-100"
                      data-testid={`income-review-toggle-${inc.id || idx}`}
                    >
                      <ReviewMarker reviewed={inc.reviewed_by_advisor} flagged={false} />
                    </button>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-slate-500 w-32">{fmtDate(inc.date)}</td>
                  <td className="px-4 py-2.5 text-sm text-slate-700">{inc.description || '—'}</td>
                  <td className="px-4 py-2.5 text-sm text-slate-500 hidden md:table-cell">{inc.category || '—'}</td>
                  <td className="px-4 py-2.5 text-sm text-right font-mono font-medium text-emerald-700">+{fmt(inc.amount)} €</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ──────── Expenses Section ────────

function ExpensesSection({ data, onReceipt, isDownloaded, canEdit, clientId, onOpen, showLocked, selectedIds, onToggleSelect, onToggleSelectAll, selectableCount = 0 }) {
  const allExpenses = data?.expenses || [];
  const visible = showLocked ? allExpenses : allExpenses.filter(e => !e.is_locked);
  const hiddenCount = allExpenses.length - visible.length;

  // Juni 2026 — drei Gruppen: aktive Belege, aktivierte Anlagen (unten), deaktivierte (ausgegraut).
  const deactivated = visible.filter(e => e.deactivated_at);
  const live = visible.filter(e => !e.deactivated_at);
  const capitalized = live.filter(e => e.treatment === 'capitalized');
  const expenses = live.filter(e => e.treatment !== 'capitalized');

  // Filter-/Zähler-Leiste: 'all' | 'active' | 'capitalized' | 'deactivated'
  const [focus, setFocus] = useState('all');
  const showFilterBar = capitalized.length > 0 || deactivated.length > 0;
  const effFocus = (focus === 'capitalized' && !capitalized.length) || (focus === 'deactivated' && !deactivated.length) || (focus === 'active' && !expenses.length) ? 'all' : focus;

  const sums = useMemo(() => {
    // Feb 2026 — Multi-VAT-Fix: getExpenseVatTotal berücksichtigt
    // tax_breakdown[] (Multi-Tax) + Auto-Compute aus amount+vat_rate.
    const brutto = expenses.reduce((s, e) => s + (e.amount || 0), 0);
    const ust = expenses.reduce((s, e) => s + getExpenseVatTotal(e), 0);
    const netto = brutto - ust;
    return { brutto, netto, ust };
  }, [expenses]);

  if (!visible.length) {
    if (hiddenCount > 0) {
      return <Empty text={`Alle ${hiddenCount} Ausgabe(n) liegen in festgeschriebenen Perioden und sind ausgeblendet.`} />;
    }
    return <Empty text="Keine Ausgaben in diesem Zeitraum." />;
  }

  return (
    <div className="space-y-3" data-testid="section-expenses">
      {hiddenCount > 0 && (
        <div className="text-xs text-slate-500 flex items-center gap-1.5">
          <Lock className="w-3 h-3" />{hiddenCount} Ausgabe(n) in festgeschriebenen Perioden ausgeblendet
        </div>
      )}
      {showFilterBar && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="expense-filter-bar">
          {[
            { key: 'all', label: 'Alle', count: visible.length, cls: 'text-slate-700 border-slate-300', active: 'bg-slate-900 text-white border-slate-900' },
            { key: 'active', label: 'Aktiv', count: expenses.length, cls: 'text-rose-700 border-rose-200', active: 'bg-rose-600 text-white border-rose-600' },
            { key: 'capitalized', label: 'Anlagen', count: capitalized.length, cls: 'text-emerald-700 border-emerald-200', active: 'bg-emerald-600 text-white border-emerald-600' },
            { key: 'deactivated', label: 'Deaktiviert', count: deactivated.length, cls: 'text-amber-700 border-amber-200', active: 'bg-amber-500 text-white border-amber-500' },
          ].map(chip => {
            const disabled = chip.key !== 'all' && chip.count === 0;
            const isActive = effFocus === chip.key;
            return (
              <button
                key={chip.key}
                type="button"
                disabled={disabled}
                onClick={() => setFocus(isActive && chip.key !== 'all' ? 'all' : chip.key)}
                data-testid={`expense-filter-${chip.key}`}
                className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${isActive ? chip.active : `bg-white hover:bg-slate-50 ${chip.cls}`}`}
              >
                {chip.label}
                <span className={`inline-flex items-center justify-center min-w-[1.25rem] px-1 rounded-full text-[11px] tabular-nums ${isActive ? 'bg-white/25' : 'bg-slate-100 text-slate-600'}`}>{chip.count}</span>
              </button>
            );
          })}
        </div>
      )}
      {(effFocus === 'all' || effFocus === 'active') && expenses.length > 0 && (
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-zinc-200 bg-zinc-50">
            <th className="text-center px-3 py-3 w-8">
              <Checkbox
                checked={selectableCount > 0 && selectedIds?.size === selectableCount}
                onCheckedChange={onToggleSelectAll}
                disabled={selectableCount === 0}
                aria-label="Alle auswählen"
                data-testid="bulk-select-all-expenses"
              />
            </th>
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3 w-8"></th>
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3">Datum</th>
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3">Beschreibung</th>
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3 hidden md:table-cell">Kategorie</th>
            <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3">Betrag</th>
            <th className="text-center text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3 w-24">Aktionen</th>
          </tr></thead>
          <tbody>
            {expenses.map(exp => {
              const reviewed = !!exp.reviewed_by_advisor;
              const flagged = (exp.open_thread_count || 0) > 0;
              return (
                <tr key={exp.id} onClick={() => onOpen(exp)}
                  className={`border-b border-zinc-100 hover:bg-rose-50/30 cursor-pointer transition-colors ${exp.is_locked ? 'opacity-60' : ''}`}
                  data-testid={`expense-row-${exp.id}`}>
                  <td className="px-3 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                    {!exp.is_locked && !reviewed ? (
                      <Checkbox
                        checked={selectedIds?.has(exp.id) || false}
                        onCheckedChange={() => onToggleSelect?.(exp.id)}
                        aria-label="Position auswählen"
                        data-testid={`bulk-select-row-${exp.id}`}
                      />
                    ) : null}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <ReviewMarker reviewed={reviewed} flagged={flagged} />
                  </td>
                  <td className="px-3 py-2.5 text-sm text-slate-500">
                    <div className="flex items-center gap-1.5">
                      {exp.is_locked && <Lock className="w-3 h-3 text-slate-400" />}
                      {fmtDate(exp.date)}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-sm text-slate-700 truncate max-w-[280px]">{exp.description || exp.vendor || '—'}</td>
                  <td className="px-3 py-2.5 text-sm text-slate-500 hidden md:table-cell">
                    <CategoryHint clientId={clientId} category={exp.category} label={catLabel(exp.category)}
                      advisorToken={localStorage.getItem('advisor_token')} />
                  </td>
                  <td className="px-3 py-2.5 text-sm text-right font-medium text-rose-700 tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    −{fmt(exp.amount)} €
                  </td>
                  <td className="px-3 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-1.5">
                      {exp.has_receipt ? (
                        <button onClick={() => onReceipt(exp.id)} className="text-emerald-600 hover:text-emerald-800" title="Beleg ansehen" data-testid={`receipt-${exp.id}`}>
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        <span className="text-rose-500" title="Beleg fehlt">
                          <AlertTriangle className="w-3.5 h-3.5" />
                        </span>
                      )}
                      {flagged && <MessageSquare className="w-3.5 h-3.5 text-blue-600" />}
                      {isDownloaded('receipt', exp.id) && <Check className="w-3.5 h-3.5 text-emerald-500" />}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-rose-50 border-t-2 border-rose-200 font-medium">
              <td colSpan="5" className="px-3 py-2.5 text-xs text-slate-700">
                Σ {expenses.length} Ausgabe{expenses.length === 1 ? '' : 'n'} · Netto {fmt(sums.netto)} € · USt {fmt(sums.ust)} €
              </td>
              <td className="px-3 py-2.5 text-sm text-right font-bold text-rose-700 tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                −{fmt(sums.brutto)} €
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      )}

      {(effFocus === 'all' || effFocus === 'capitalized') && capitalized.length > 0 && (
        <div data-testid="section-capitalized-assets" className="space-y-1.5">
          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800 pt-1">
            <Boxes className="w-4 h-4" />Aktivierte Anlagen
            <span className="text-xs font-normal text-slate-500">({capitalized.length}) — werden über AfA periodisiert</span>
          </div>
          <ExpenseTableBlock rows={capitalized} variant="capitalized" clientId={clientId} onOpen={onOpen} onReceipt={onReceipt} isDownloaded={isDownloaded} />
        </div>
      )}

      {(effFocus === 'all' || effFocus === 'deactivated') && deactivated.length > 0 && (
        <div data-testid="section-deactivated-expenses" className="space-y-1.5">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-800 pt-1">
            <Ban className="w-4 h-4" />Deaktivierte Belege
            <span className="text-xs font-normal text-slate-500">({deactivated.length}) — zählen nicht in UVA, Gewinn, Abschluss oder Export</span>
          </div>
          <ExpenseTableBlock rows={deactivated} variant="deactivated" clientId={clientId} onOpen={onOpen} onReceipt={onReceipt} isDownloaded={isDownloaded} />
        </div>
      )}
    </div>
  );
}

// ──────── Read-only Belegtabelle für „Aktivierte Anlagen" + „Deaktivierte Belege" ────────

function ExpenseTableBlock({ rows, variant, clientId, onOpen, onReceipt, isDownloaded }) {
  const isDeactivated = variant === 'deactivated';
  const wrapBorder = isDeactivated ? 'border-amber-200' : 'border-emerald-200';
  const headBg = isDeactivated ? 'bg-amber-50' : 'bg-emerald-50';
  return (
    <div className={`bg-white border ${wrapBorder} rounded-xl overflow-hidden`}>
      <table className="w-full">
        <thead><tr className={`border-b border-zinc-200 ${headBg}`}>
          <th className="text-left px-3 py-3 w-8"></th>
          <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3">Datum</th>
          <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3">Beschreibung</th>
          <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3 hidden md:table-cell">Kategorie</th>
          <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3">Betrag</th>
          <th className="text-center text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-3 w-20">Beleg</th>
        </tr></thead>
        <tbody>
          {rows.map(exp => (
            <tr key={exp.id} onClick={() => onOpen(exp)}
              className={`border-b border-zinc-100 cursor-pointer transition-colors ${isDeactivated ? 'opacity-50 hover:opacity-75 bg-amber-50/20' : 'hover:bg-emerald-50/30'}`}
              data-testid={`expense-row-${exp.id}`}>
              <td className="px-3 py-2.5 text-center">
                {isDeactivated
                  ? <Ban className="w-4 h-4 text-amber-500 mx-auto" title="Deaktiviert" />
                  : <Boxes className="w-4 h-4 text-emerald-600 mx-auto" title="Aktivierte Anlage" />}
              </td>
              <td className="px-3 py-2.5 text-sm text-slate-500">{fmtDate(exp.date)}</td>
              <td className="px-3 py-2.5 text-sm text-slate-700 max-w-[280px]">
                <div className={`truncate ${isDeactivated ? 'line-through text-slate-400' : ''}`}>{exp.description || exp.vendor || '—'}</div>
                {isDeactivated && (
                  <div className="text-[11px] text-amber-700 truncate" data-testid={`deactivated-reason-${exp.id}`}>
                    Deaktiviert{exp.deactivated_by_name ? ` · ${exp.deactivated_by_name}` : ''}{exp.deactivated_at ? ` · ${fmtDate(exp.deactivated_at)}` : ''}{exp.deactivated_reason ? ` · „${exp.deactivated_reason}"` : ''}
                  </div>
                )}
              </td>
              <td className="px-3 py-2.5 text-sm text-slate-500 hidden md:table-cell">{catLabel(exp.category)}</td>
              <td className={`px-3 py-2.5 text-sm text-right font-medium tabular-nums ${isDeactivated ? 'text-slate-400 line-through' : 'text-emerald-700'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                −{fmt(exp.amount)} €
              </td>
              <td className="px-3 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-center gap-1.5">
                  {exp.has_receipt ? (
                    <button onClick={() => onReceipt(exp.id)} className="text-emerald-600 hover:text-emerald-800" title="Beleg ansehen" data-testid={`receipt-${exp.id}`}>
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <span className="text-rose-500" title="Beleg fehlt"><AlertTriangle className="w-3.5 h-3.5" /></span>
                  )}
                  {isDownloaded && isDownloaded('receipt', exp.id) && <Check className="w-3.5 h-3.5 text-emerald-500" />}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReviewMarker({ reviewed, flagged }) {
  if (reviewed) return <CheckCircle2 className="w-4 h-4 text-emerald-600 mx-auto" title="Geprüft" data-testid="review-marker-reviewed" />;
  if (flagged) return <AlertCircle className="w-4 h-4 text-orange-500 mx-auto" title="Rückfrage offen" data-testid="review-marker-flagged" />;
  return <span className="w-2 h-2 rounded-full bg-zinc-300 inline-block" data-testid="review-marker-pending" />;
}

// ──────── PeriodReviewBar (uses display_state) ────────
//
// C2-Cleanup (Feb 2026): Periode hat zwei Status-Felder:
//   • status         — Workflow-Zustand (open|in_review|feedback|approved|locked)
//   • display_state  — aggregierte UI-Anzeige (in_preparation|feedback|in_review|completed)
// Wir zeigen ausschließlich `display_state` an. Das Backend liefert beides.

const DISPLAY_STATE_CSS = {
  in_preparation: 'bg-zinc-100 text-zinc-600 border-zinc-300',
  feedback: 'bg-orange-50 text-orange-700 border-orange-300',
  in_review: 'bg-amber-50 text-amber-700 border-amber-300',
  completed: 'bg-slate-900 text-white border-slate-900',
};
const DISPLAY_STATE_DOT = {
  in_preparation: 'bg-zinc-400',
  feedback: 'bg-orange-500',
  in_review: 'bg-amber-500',
  completed: 'bg-white',
};
const DISPLAY_STATE_LABEL = {
  in_preparation: 'In Vorbereitung',
  feedback: 'Rückfragen',
  in_review: 'In Prüfung',
  completed: 'Abgeschlossen',
};
const MONTH_NAMES = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

function PeriodReviewBar({ clientId, year, onYearChange, canEdit = false }) {
  const currentYear = new Date().getFullYear();
  const YEAR_OPTIONS = Array.from({ length: 7 }, (_, i) => currentYear - i);
  const [reviews, setReviews] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ period_type: 'quarter', period_value: 1, notes: '' });
  const [closureFor, setClosureFor] = useState(null);

  const fetchReviews = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/advisor/clients/${clientId}/period-reviews?year=${year}`, { headers: H() });
      setReviews(r.data.reviews || []);
    } catch {}
  }, [clientId, year]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  // Prüf-Ampel (Juni 2026): Befund-Zähler pro Periode aus dem Batch-Precheck —
  // der SB sieht ohne Klick, welche Periode abschlussreif ist.
  const [checkSummaries, setCheckSummaries] = useState({});
  useEffect(() => {
    if (reviews.length === 0) { setCheckSummaries({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.post(`${API}/advisor/clients/${clientId}/period-precheck/batch`, {
          periods: reviews.map((rv) => ({
            year: rv.year,
            period_type: rv.period_type,
            period_value: rv.period_type === 'year' ? null : rv.period_value,
          })),
        }, { headers: H() });
        if (cancelled) return;
        const map = {};
        for (const s of r.data.summaries || []) {
          map[`${s.year}-${s.period_type}-${s.period_value ?? ''}`] = s;
        }
        setCheckSummaries(map);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [reviews, clientId]);

  const handleCreate = async () => {
    if (form.period_type === 'year' && year >= currentYear) {
      toast.error(`Ein Jahresabschluss für ${year} ist erst im Folgejahr möglich.`);
      return;
    }
    try {
      await axios.post(`${API}/advisor/clients/${clientId}/period-reviews`, {
        year,
        period_type: form.period_type,
        period_value: form.period_type !== 'year' ? form.period_value : undefined,
        notes: form.notes || undefined,
      }, { headers: H() });
      toast.success('Periode erstellt');
      setShowCreate(false);
      setForm({ period_type: 'quarter', period_value: 1, notes: '' });
      fetchReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Diese Periode wirklich löschen? Gespeicherte Anmerkungen bleiben erhalten.')) return;
    try {
      await axios.delete(`${API}/advisor/period-reviews/${reviewId}`, { headers: H() });
      toast.success('Periode gelöscht');
      fetchReviews();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  const periodLabel = (r) => {
    if (r.period_type === 'year') return `${r.year}`;
    if (r.period_type === 'quarter') return `Q${r.period_value} ${r.year}`;
    if (r.period_type === 'month') return `${MONTH_NAMES[(r.period_value || 1) - 1]} ${r.year}`;
    return `${r.year}`;
  };

  const yearClosureBlocked = form.period_type === 'year' && year >= currentYear;

  if (reviews.length === 0 && !showCreate) {
    return (
      <div className="mb-4 flex items-center justify-between bg-white border border-zinc-200 rounded-xl px-4 py-3" data-testid="period-review-bar">
        <span className="text-xs text-slate-500">Keine Perioden für {year}</span>
        <Button size="sm" onClick={() => setShowCreate(true)} className="rounded-md text-xs bg-slate-900 hover:bg-slate-800 text-white h-7" data-testid="create-period-btn">
          <Plus className="w-3 h-3 mr-1" />Periode anlegen
        </Button>
      </div>
    );
  }

  return (
    <div className="mb-4 space-y-2" data-testid="period-review-bar">
      <div className="bg-white border border-zinc-200 rounded-xl">
        <div className="px-4 py-2.5 border-b border-zinc-100 flex items-center justify-between">
          <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Perioden {year}</span>
          <Button size="sm" variant="outline" onClick={() => setShowCreate(!showCreate)} className="rounded-md text-xs h-7" data-testid="create-period-btn">
            <Plus className="w-3 h-3 mr-1" />{showCreate ? 'Abbrechen' : 'Periode hinzufügen'}
          </Button>
        </div>
        {reviews.map(r => {
          const ds = r.display_state || 'in_preparation';
          return (
            <div key={r.id} className="px-4 py-3 border-b border-zinc-100 last:border-0 flex items-center gap-4" data-testid={`period-${r.id}`}>
              <div className="w-24 text-sm font-medium text-slate-900 shrink-0 flex items-center gap-1.5">
                {ds === 'completed' && <Lock className="w-3 h-3 text-slate-700" />}
                {periodLabel(r)}
              </div>
              <div className="flex-1 flex items-center gap-2 flex-wrap">
                <span className={`text-xs px-2.5 py-1 border rounded flex items-center gap-1.5 ${DISPLAY_STATE_CSS[ds]}`}
                  data-testid={`period-status-${r.id}`}>
                  <span className={`w-2 h-2 rounded-full ${DISPLAY_STATE_DOT[ds]}`} />
                  {DISPLAY_STATE_LABEL[ds]}
                </span>
                {r.open_threads_count > 0 && (
                  <span className="text-[11px] text-orange-700 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded flex items-center gap-1" title="Offene Rückfragen in dieser Periode">
                    <MessageSquare className="w-3 h-3" />{r.open_threads_count}
                  </span>
                )}
                {r.total_count > 0 && (
                  <span className="text-[11px] text-slate-500 bg-zinc-50 border border-zinc-200 px-1.5 py-0.5 rounded">
                    {r.reviewed_count}/{r.total_count} geprüft
                  </span>
                )}
                {(() => {
                  const s = checkSummaries[`${r.year}-${r.period_type}-${r.period_type === 'year' ? '' : (r.period_value ?? '')}`];
                  if (!s) return null;
                  if (s.errors > 0 || s.warnings > 0) {
                    return (
                      <span
                        className={`text-[11px] px-1.5 py-0.5 rounded border flex items-center gap-1 ${
                          s.errors > 0
                            ? 'text-rose-700 bg-rose-50 border-rose-200'
                            : 'text-amber-700 bg-amber-50 border-amber-200'
                        }`}
                        title="Beleg-Prüfung: offene Befunde — Details unten in der Prüfansicht"
                        data-testid={`period-check-badge-${r.id}`}
                      >
                        <AlertTriangle className="w-3 h-3" />
                        {s.errors > 0 ? `${s.errors} Fehler` : ''}
                        {s.errors > 0 && s.warnings > 0 ? ' · ' : ''}
                        {s.warnings > 0 ? `${s.warnings} Warnungen` : ''}
                      </span>
                    );
                  }
                  return (
                    <span
                      className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded flex items-center gap-1"
                      title="Beleg-Prüfung: keine Fehler oder Warnungen"
                      data-testid={`period-check-badge-${r.id}`}
                    >
                      <CheckCircle2 className="w-3 h-3" />abschlussreif
                    </span>
                  );
                })()}
                {r.notes && <span className="text-xs text-slate-400 truncate max-w-xs" title={r.notes}>{r.notes}</span>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => setClosureFor(r)}
                  className={`text-xs px-3 py-1 rounded-md transition-colors flex items-center gap-1 font-medium shadow-sm ${
                    ds === 'completed'
                      ? 'bg-slate-700 hover:bg-slate-600 text-white'
                      : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  }`}
                  title={ds === 'completed' ? 'Abschlussbericht ansehen' : (r.has_closure ? 'Begonnenen Abschluss fortsetzen' : 'Periodenabschluss starten')}
                  data-testid={`open-closure-${r.id}`}>
                  <ClipboardCheck className="w-3.5 h-3.5" />
                  {ds === 'completed' ? 'Bericht' : (r.has_closure ? 'Abschluss fortsetzen' : 'Abschluss starten')}
                </button>
                {ds !== 'completed' && (
                  <button onClick={() => handleDelete(r.id)} className="text-slate-400 hover:text-red-500 p-1" data-testid={`delete-period-${r.id}`}>
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showCreate && (
        <div className="bg-white border border-zinc-200 rounded-xl p-4" data-testid="period-create-form">
          <div className="grid grid-cols-5 gap-3 items-end">
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Jahr</Label>
              <Select value={String(year)} onValueChange={v => onYearChange && onYearChange(parseInt(v))}>
                <SelectTrigger className="h-8 text-sm rounded-md" data-testid="period-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>{YEAR_OPTIONS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Typ</Label>
              <Select value={form.period_type} onValueChange={v => setForm(f => ({ ...f, period_type: v, period_value: v === 'quarter' ? 1 : v === 'month' ? 1 : null }))}>
                <SelectTrigger className="h-8 text-sm rounded-md" data-testid="period-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Monat</SelectItem>
                  <SelectItem value="quarter">Quartal</SelectItem>
                  <SelectItem value="year">Ganzes Jahr (Jahresabschluss)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.period_type === 'quarter' && (
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Quartal</Label>
                <Select value={String(form.period_value)} onValueChange={v => setForm(f => ({ ...f, period_value: parseInt(v) }))}>
                  <SelectTrigger className="h-8 text-sm rounded-md" data-testid="period-quarter-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{[1,2,3,4].map(q => <SelectItem key={q} value={String(q)}>Q{q}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {form.period_type === 'month' && (
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Monat</Label>
                <Select value={String(form.period_value)} onValueChange={v => setForm(f => ({ ...f, period_value: parseInt(v) }))}>
                  <SelectTrigger className="h-8 text-sm rounded-md" data-testid="period-month-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTH_NAMES.map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Notiz</Label>
              <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Optional" className="h-8 text-sm rounded-md" data-testid="period-notes-input" />
            </div>
            <Button size="sm" onClick={handleCreate} disabled={yearClosureBlocked}
              className="bg-slate-900 hover:bg-slate-800 text-white rounded-md text-xs h-8 disabled:opacity-50" data-testid="period-submit-btn">
              Anlegen
            </Button>
          </div>
          {yearClosureBlocked && (
            <p className="text-[11px] text-amber-700 mt-2 flex items-center gap-1" data-testid="year-closure-blocked-hint">
              <AlertTriangle className="w-3 h-3" />
              Ein Jahresabschluss für {year} ist erst im Folgejahr ({year + 1}) möglich.
            </p>
          )}
        </div>
      )}

      {closureFor && (
        <PeriodClosureWizard
          open={!!closureFor}
          onClose={() => { setClosureFor(null); fetchReviews(); }}
          clientId={clientId}
          periodReview={closureFor}
          canEdit={canEdit}
          onFinalized={() => { setClosureFor(null); fetchReviews(); }}
        />
      )}
    </div>
  );
}

function Empty({ text }) { return <div className="bg-white border border-zinc-200 rounded-xl p-12 text-center text-sm text-slate-500">{text}</div>; }

function SubTabs({ subs, value, onChange, testIdPrefix }) {
  return (
    <div className="flex gap-1 mb-4 bg-zinc-100 p-1 rounded-lg w-fit" data-testid={`${testIdPrefix}-tabs`}>
      {subs.map(({ id, label, icon: Icon }) => (
        <button key={id} onClick={() => onChange(id)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            value === id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid={`${testIdPrefix}-${id}`}>
          {Icon && <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />}
          {label}
        </button>
      ))}
    </div>
  );
}

// ──────── Export Dialog (unchanged) ────────

function ExportDialog({ clientId, clientName, clientCountry, year, onClose, onTrack, onOpenExpense }) {
  // Juni 2026 — vereinheitlicht mit FL-Engine: neue Formate (BMD/DATEV-AT/DBM-V2)
  // laufen über /advisor/clients/{cid}/exports/{fmt} (identisch zu FL), Legacy
  // (csv/datev/zip) über /advisor/clients/{cid}/export.
  const NEW_ENGINE_FORMATS = ['bmd', 'datev_at', 'dbm_v2'];
  const FORMAT_OPTIONS = [
    { v: 'datev_at', l: 'DATEV-AT', d: 'Buchungsstapel (AT-EKR)', show: clientCountry === 'AT' },
    { v: 'bmd', l: 'BMD-XML', d: 'BMD NTCS (AT)', show: clientCountry === 'AT' },
    { v: 'dbm_v2', l: 'DATEV Belegtransfer', d: 'DBM-V2 + PDFs (DE)', show: clientCountry === 'DE' },
    { v: 'datev', l: 'DATEV-EXTF', d: 'Buchungsstapel (DE)', show: clientCountry === 'DE' },
    { v: 'csv', l: 'CSV', d: 'Semikolon-getrennt' },
    { v: 'zip', l: 'ZIP', d: 'CSVs + PDFs + Belege' },
  ].filter((f) => f.show !== false);
  const defaultFormat = clientCountry === 'AT' ? 'datev_at' : clientCountry === 'DE' ? 'dbm_v2' : 'csv';
  const [format, setFormat] = useState(defaultFormat);
  const [incInv, setIncInv] = useState(true);
  const [incExp, setIncExp] = useState(true);
  const [incRec, setIncRec] = useState(false);
  const [period, setPeriod] = useState('year');
  const [qMonth, setQMonth] = useState(1);
  const [qQuarter, setQQuarter] = useState(1);
  const [dateFrom, setDateFrom] = useState(`${year}-01-01`);
  const [dateTo, setDateTo] = useState(`${year}-12-31`);
  const [exporting, setExporting] = useState(false);
  // Feb 2026 — Pre-Export Compliance-Check
  const [compliance, setCompliance] = useState(null);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [expandedIssue, setExpandedIssue] = useState(null);
  const [sentIssues, setSentIssues] = useState({}); // { issue_code: { count, when } }
  const [sendingIssue, setSendingIssue] = useState(null);

  useEffect(() => {
    if (period === 'year') { setDateFrom(`${year}-01-01`); setDateTo(`${year}-12-31`); }
    else if (period === 'quarter') {
      const m = (qQuarter - 1) * 3 + 1;
      setDateFrom(`${year}-${String(m).padStart(2, '0')}-01`);
      const endM = m + 2; const endD = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][endM];
      setDateTo(`${year}-${String(endM).padStart(2, '0')}-${endD}`);
    } else if (period === 'month') {
      setDateFrom(`${year}-${String(qMonth).padStart(2, '0')}-01`);
      const endD = new Date(year, qMonth, 0).getDate();
      setDateTo(`${year}-${String(qMonth).padStart(2, '0')}-${endD}`);
    }
  }, [period, year, qMonth, qQuarter]);

  // Feb 2026 — Compliance-Check trigger bei Datums-/Inhalts-Änderung
  useEffect(() => {
    if (!incExp && !incInv) return;
    let cancelled = false;
    setComplianceLoading(true);
    axios.get(`${API}/advisor/clients/${clientId}/compliance-check`, {
      headers: H(),
      params: { date_from: dateFrom, date_to: dateTo, include_expenses: incExp, include_invoices: incInv },
    })
      .then((r) => { if (!cancelled) setCompliance(r.data); })
      .catch(() => { if (!cancelled) setCompliance(null); })
      .finally(() => { if (!cancelled) setComplianceLoading(false); });
    return () => { cancelled = true; };
  }, [clientId, dateFrom, dateTo, incExp, incInv]);

  const isNewEngine = NEW_ENGINE_FORMATS.includes(format);

  const handleExport = async () => {
    // Neue Engine unterstützt nur Jahr/Quartal/Monat (kein freier Zeitraum)
    if (isNewEngine && period === 'custom') {
      toast.error('BMD / DATEV-AT / Belegtransfer benötigen Jahr, Quartal oder Monat (kein eigener Zeitraum).');
      return;
    }
    setExporting(true);
    try {
      let res, fname;
      if (isNewEngine) {
        const period_type = period === 'year' ? 'yearly' : period === 'quarter' ? 'quarterly' : 'monthly';
        const period_index = period === 'quarter' ? qQuarter : period === 'month' ? qMonth : null;
        res = await axios.post(`${API}/advisor/clients/${clientId}/exports/${format}`,
          { period_type, year, period_index },
          { headers: H(), responseType: 'blob' });
      } else {
        res = await axios.post(`${API}/advisor/clients/${clientId}/export`,
          { format, date_from: dateFrom, date_to: dateTo, include_invoices: incInv, include_expenses: incExp, include_receipts: incRec },
          { headers: H(), responseType: 'blob' });
      }
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?(.+?)"?$/);
      const extMap = { datev: 'csv', datev_at: 'csv', bmd: 'xml', dbm_v2: 'zip' };
      fname = m ? m[1] : `${clientName || 'export'}_${dateFrom}_${dateTo}.${extMap[format] || format}`;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([res.data])); a.download = fname; a.click();
      onTrack('export', `${format}_${dateFrom}_${dateTo}`, format);
      toast.success('Export heruntergeladen');
      onClose();
    } catch (err) {
      // Blob-Fehlerantwort ggf. als Text auslesen
      let msg = 'Export fehlgeschlagen';
      try {
        if (err.response?.data instanceof Blob) {
          const txt = await err.response.data.text();
          msg = JSON.parse(txt).detail || msg;
        } else {
          msg = err.response?.data?.detail || msg;
        }
      } catch { /* ignore */ }
      toast.error(msg);
    }
    finally { setExporting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="export-dialog">
      <div className="bg-white w-full max-w-lg border border-zinc-200 rounded-xl shadow-lg">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Daten exportieren</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-5">
          <div>
            <div className="text-xs font-medium text-slate-700 uppercase tracking-wider mb-2">Zeitraum</div>
            <div className="flex gap-2 mb-3">
              {[{ v: 'year', l: 'Jahr' }, { v: 'quarter', l: 'Quartal' }, { v: 'month', l: 'Monat' }, { v: 'custom', l: 'Eigener' }].map(({ v, l }) => (
                <button key={v} onClick={() => setPeriod(v)}
                  className={`px-3 py-1.5 text-xs border transition-colors ${period === v ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-zinc-200 hover:bg-zinc-50'}`}
                  data-testid={`period-${v}`}>{l}</button>
              ))}
            </div>
            {period === 'quarter' && (
              <div className="flex gap-2">
                {[1, 2, 3, 4].map(q => (
                  <button key={q} onClick={() => setQQuarter(q)}
                    className={`px-3 py-1.5 text-xs border ${qQuarter === q ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-zinc-200'}`}>
                    Q{q}
                  </button>
                ))}
              </div>
            )}
            {period === 'month' && (
              <select value={qMonth} onChange={e => setQMonth(Number(e.target.value))} className="border border-zinc-200 text-sm px-3 py-1.5 w-full">
                {['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'].map((m, i) => (
                  <option key={i} value={i + 1}>{m}</option>
                ))}
              </select>
            )}
            {period === 'custom' && (
              <div className="flex gap-2 items-center">
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="border border-zinc-200 text-sm px-2 py-1.5 flex-1" />
                <span className="text-xs text-slate-400">bis</span>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="border border-zinc-200 text-sm px-2 py-1.5 flex-1" />
              </div>
            )}
            <div className="text-xs text-slate-400 mt-1">{dateFrom} — {dateTo}</div>
          </div>
          <div>
            <div className="text-xs font-medium text-slate-700 uppercase tracking-wider mb-2">Inhalt</div>
            <div className="space-y-2">
              {[
                { key: 'inv', val: incInv, set: setIncInv, label: 'Rechnungen (Einnahmen)' },
                { key: 'exp', val: incExp, set: setIncExp, label: 'Ausgaben' },
                { key: 'rec', val: incRec, set: setIncRec, label: 'Beleg-Dateien (Fotos/PDFs)', note: 'Nur bei ZIP' },
              ].map(({ key, val, set, label, note }) => (
                <button key={key} onClick={() => set(!val)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 border text-left text-sm transition-colors ${
                    val ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-white border-zinc-200 text-slate-500'}`}
                  data-testid={`content-${key}`}>
                  <div className={`w-4 h-4 border flex items-center justify-center ${val ? 'bg-emerald-600 border-emerald-600' : 'border-zinc-300'}`}>
                    {val && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div>{label}{note && <span className="text-xs text-slate-400 ml-1">({note})</span>}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-slate-700 uppercase tracking-wider mb-2">Format</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {FORMAT_OPTIONS.map(({ v, l, d }) => (
                <button key={v} onClick={() => setFormat(v)}
                  className={`p-3 border text-left transition-colors ${format === v ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-zinc-200 hover:bg-zinc-50'}`}
                  data-testid={`format-${v}`}>
                  <div className="text-sm font-medium">{l}</div>
                  <div className={`text-xs mt-0.5 ${format === v ? 'text-slate-300' : 'text-slate-400'}`}>{d}</div>
                </button>
              ))}
            </div>
            {isNewEngine && (
              <p className="text-[11px] text-slate-500 mt-2 leading-snug" data-testid="new-engine-note">
                ℹ️ Identisch zum FL-Export. Inhalt (Rechnungen + Ausgaben{format === 'dbm_v2' ? ' + Belege' : ''})
                wird automatisch übernommen; Zeitraum nur als Jahr/Quartal/Monat.
              </p>
            )}
          </div>
          {/* Compliance-Banner */}
          {(incExp || incInv) && (
            <div data-testid="compliance-check-section">
              <div className="text-xs font-medium text-slate-700 uppercase tracking-wider mb-2 flex items-center gap-2">
                <span>Compliance-Check</span>
                {complianceLoading && <span className="text-[10px] text-slate-400 normal-case font-normal">prüft…</span>}
              </div>
              {!complianceLoading && compliance && (() => {
                const { ok, summary, issues } = compliance;
                if (ok) {
                  return (
                    <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm" data-testid="compliance-ok">
                      <span className="text-emerald-700 font-semibold">✓ Keine kritischen Probleme</span>
                      {summary.info > 0 && (
                        <span className="text-emerald-600 ml-2 text-xs">· {summary.info} optionale Hinweise</span>
                      )}
                    </div>
                  );
                }
                return (
                  <div className="space-y-1.5" data-testid="compliance-issues">
                    {issues.map((iss) => {
                      const isCrit = iss.severity === 'critical';
                      const isWarn = iss.severity === 'warning';
                      const expanded = expandedIssue === iss.code;
                      const palette = isCrit
                        ? 'border-rose-300 bg-rose-50 text-rose-900'
                        : isWarn
                          ? 'border-amber-300 bg-amber-50 text-amber-900'
                          : 'border-slate-200 bg-slate-50 text-slate-700';
                      return (
                        <div key={iss.code} className={`rounded-lg border-2 ${palette}`} data-testid={`compliance-issue-${iss.code}`}>
                          <button
                            type="button"
                            onClick={() => setExpandedIssue(expanded ? null : iss.code)}
                            className="w-full text-left px-3 py-2 flex items-center justify-between gap-2"
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className="text-base shrink-0">{isCrit ? '✗' : isWarn ? '⚠' : 'ℹ'}</span>
                              <span className="text-sm font-semibold truncate">{iss.title}</span>
                            </div>
                            <span className="text-[10px] uppercase tracking-wider opacity-70 shrink-0">
                              {expanded ? '▲ Details' : '▼ Details'}
                            </span>
                          </button>
                          {expanded && (
                            <div className="px-3 pb-3 pt-1 border-t border-current/10 space-y-2 text-xs">
                              <p className="opacity-90">{iss.description}</p>
                              <p className="opacity-80 italic">→ {iss.action_hint}</p>

                              {/* Feb 2026 — FL anfragen Bulk-Action (nur wenn Belege mit ID vorhanden) */}
                              {iss.items && iss.items.some(i => i.id) && iss.code !== 'uncategorized' && (
                                <div className="flex items-center justify-between gap-2 pt-1">
                                  {sentIssues[iss.code] ? (
                                    <div className="text-[11px] font-medium opacity-90 flex items-center gap-1.5" data-testid={`compliance-sent-${iss.code}`}>
                                      ✓ {sentIssues[iss.code].count} Anfrage{sentIssues[iss.code].count !== 1 ? 'n' : ''} im FL-Posteingang erstellt
                                    </div>
                                  ) : (
                                    <Button
                                      type="button"
                                      size="sm"
                                      onClick={async () => {
                                        const ids = iss.items.filter(i => i.id).map(i => i.id);
                                        if (!ids.length) return;
                                        setSendingIssue(iss.code);
                                        try {
                                          const r = await axios.post(
                                            `${API}/advisor/clients/${clientId}/compliance-request`,
                                            { issue_code: iss.code, expense_ids: ids },
                                            { headers: H() }
                                          );
                                          setSentIssues((prev) => ({
                                            ...prev,
                                            [iss.code]: { count: r.data?.created || ids.length, when: Date.now() },
                                          }));
                                          toast.success(r.data?.message || `${ids.length} Anfrage(n) gesendet`);
                                        } catch (e) {
                                          toast.error(e.response?.data?.detail || 'Anfrage fehlgeschlagen');
                                        } finally {
                                          setSendingIssue(null);
                                        }
                                      }}
                                      disabled={sendingIssue === iss.code}
                                      className="h-7 text-[11px] bg-white/80 hover:bg-white text-slate-900 border border-current/30"
                                      data-testid={`compliance-bulk-request-${iss.code}`}
                                    >
                                      {sendingIssue === iss.code ? '⏳ Sende…' : `📩 FL anfragen (${iss.items.filter(i => i.id).length} Beleg${iss.items.filter(i => i.id).length !== 1 ? 'e' : ''})`}
                                    </Button>
                                  )}
                                </div>
                              )}

                              {iss.items && iss.items.length > 0 && (
                                <div className="bg-white/50 rounded px-2 py-1.5 max-h-40 overflow-y-auto">
                                  {iss.items.map((item, idx) => {
                                    const clickable = !!item.id && !!onOpenExpense;
                                    const RowEl = clickable ? 'button' : 'div';
                                    return (
                                      <RowEl
                                        key={idx}
                                        type={clickable ? 'button' : undefined}
                                        onClick={clickable ? () => onOpenExpense(item.id) : undefined}
                                        className={`w-full flex items-center justify-between gap-2 py-0.5 border-b border-current/5 last:border-0 text-left ${
                                          clickable ? 'hover:bg-white/80 -mx-2 px-2 rounded transition cursor-pointer' : ''
                                        }`}
                                        data-testid={clickable ? `compliance-fix-${item.id}` : undefined}
                                        title={clickable ? 'Beleg im Drawer öffnen und korrigieren' : undefined}
                                      >
                                        <div className="min-w-0 flex-1">
                                          <span className="font-medium">{item.vendor || 'Unbekannt'}</span>
                                          <span className="opacity-60 ml-2">{item.date}</span>
                                          {item.missing && (
                                            <span className="opacity-70 text-[10px] ml-2">→ {item.missing.join(', ')} fehlt</span>
                                          )}
                                          {item.diff !== undefined && (
                                            <span className="opacity-70 text-[10px] ml-2">→ Δ {item.diff > 0 ? '+' : ''}{item.diff.toFixed(2)} €</span>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                          <span className="font-semibold">{Number(item.amount || 0).toFixed(2)} €</span>
                                          {clickable && <span className="text-[10px] opacity-50">↗</span>}
                                        </div>
                                      </RowEl>
                                    );
                                  })}
                                  {iss.total_items > iss.items.length && (
                                    <div className="text-[10px] opacity-60 pt-1 text-center">… und {iss.total_items - iss.items.length} weitere</div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-3 items-center">
          {/* Feb 2026 — Bei kritischen Compliance-Issues: rote Warnung im Footer */}
          {compliance && compliance.summary?.critical > 0 && (
            <div className="text-xs text-rose-700 font-medium mr-auto" data-testid="compliance-footer-warning">
              ⚠ Export trotz {compliance.summary.critical} kritischer Probleme?
            </div>
          )}
          <Button variant="outline" onClick={onClose} className="rounded-md" data-testid="export-cancel">Abbrechen</Button>
          <Button onClick={handleExport} disabled={exporting || (!incInv && !incExp)}
            className={`text-white rounded-md ${compliance?.summary?.critical > 0 ? 'bg-rose-600 hover:bg-rose-700' : 'bg-slate-900 hover:bg-slate-800'}`}
            data-testid="export-submit">
            {exporting ? 'Exportiert...' : (
              <>
                <Download className="w-4 h-4 mr-1.5" />
                {compliance?.summary?.critical > 0 ? 'Trotzdem exportieren' : 'Herunterladen'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ──────── Audit Log Tab (C10-Cleanup, Feb 2026) ────────
//
// Zeigt advisor_audit_log: SB-Aktionen (Edit, Status-Wechsel, paid_at, Review-Toggle,
// Period-Lock/Unlock, Korrekturen). Filterbar nach Entity-Typ und Datums-Bereich.

const AUDIT_FIELD_LABELS = {
  status: 'Status',
  paid_at: 'Bezahldatum',
  review: 'Geprüft-Markierung',
  reviewed_by_advisor: 'Geprüft-Markierung',
  category: 'Kategorie',
  description: 'Beschreibung',
  amount: 'Betrag',
  vat_rate: 'USt-Satz',
  date: 'Datum',
  invoice_date: 'Rechnungsdatum',
  vendor: 'Lieferant',
  receipt: 'Beleg',
  lock: 'Sperrung',
  unlock: 'Entsperrung',
  correction: 'Korrekturrechnung erstellt',
  storno: 'Stornorechnung erstellt',
  pauschal_variante: 'Pauschal-Variante (§ 17 EStG)',
  pauschal_vermerk: '6 %-Vermerk (Begründung)',
};

const AUDIT_ENTITY_LABELS = {
  invoice: 'Rechnung',
  expense: 'Ausgabe',
  income: 'Einnahme',
  period: 'Periode',
  receipt: 'Beleg',
  yearend: 'Jahresabschluss',
  tax_profile: 'Stammdaten',
};

function AuditLogTab({ clientId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/advisor/audit-log/${clientId}`, { headers: H() });
        if (!cancel) setEntries(r.data.entries || []);
      } catch {
        if (!cancel) setEntries([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [clientId]);

  const types = useMemo(() => {
    const set = new Set(entries.map(e => e.entity_type).filter(Boolean));
    return ['all', ...Array.from(set)];
  }, [entries]);

  const filtered = useMemo(() => {
    if (filterType === 'all') return entries;
    return entries.filter(e => e.entity_type === filterType);
  }, [entries, filterType]);

  if (loading) {
    return <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-sm text-slate-500">Laden...</div>;
  }

  return (
    <div className="space-y-3" data-testid="audit-log-tab">
      <div className="flex items-center gap-2 flex-wrap">
        {types.map(t => (
          <button key={t} onClick={() => setFilterType(t)}
            className={`text-xs px-3 py-1.5 border rounded-md transition-colors ${
              filterType === t ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-zinc-200 hover:bg-zinc-50'
            }`}
            data-testid={`audit-filter-${t}`}>
            {t === 'all' ? `Alle (${entries.length})` : AUDIT_ENTITY_LABELS[t] || t}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Empty text={filterType === 'all' ? 'Noch keine SB-Aktionen protokolliert.' : 'Keine Einträge zu diesem Filter.'} />
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl divide-y divide-zinc-100" data-testid="audit-log-list">
          {filtered.map((e, i) => (
            <AuditEntry key={e.id || `audit-${i}`} entry={e} />
          ))}
        </div>
      )}
    </div>
  );
}

function AuditEntry({ entry }) {
  const fieldLabel = AUDIT_FIELD_LABELS[entry.field] || entry.field || 'Änderung';
  const entityLabel = AUDIT_ENTITY_LABELS[entry.entity_type] || entry.entity_type;
  const when = entry.created_at ? new Date(entry.created_at).toLocaleString('de-DE') : '—';
  const ref = entry.extra?.invoice_number || entry.extra?.expense_description || entry.extra?.year || entry.entity_id?.slice(0, 8);
  return (
    <div className="px-4 py-3 flex items-start gap-3 hover:bg-zinc-50/50 transition-colors" data-testid={`audit-entry-${entry.id}`}>
      <div className="mt-0.5 w-8 h-8 bg-zinc-100 rounded-full flex items-center justify-center shrink-0">
        <History className="w-4 h-4 text-slate-500" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap text-sm">
          <span className="font-medium text-slate-900">{entry.advisor_name || 'Steuerberater'}</span>
          <span className="text-slate-500">änderte</span>
          <span className="text-slate-700 font-medium">{fieldLabel}</span>
          {entityLabel && <span className="text-xs px-2 py-0.5 bg-zinc-100 text-slate-600 rounded">{entityLabel}{ref ? ` · ${ref}` : ''}</span>}
        </div>
        {(entry.old_value !== undefined || entry.new_value !== undefined) && (
          <div className="mt-1 flex items-center gap-2 text-xs text-slate-600 flex-wrap">
            {entry.old_value !== undefined && entry.old_value !== null && entry.old_value !== '' && (
              <span className="line-through text-slate-400">{String(entry.old_value)}</span>
            )}
            {entry.old_value !== undefined && entry.new_value !== undefined && <ChevronRight className="w-3 h-3 text-slate-300" />}
            {entry.new_value !== undefined && entry.new_value !== null && entry.new_value !== '' && (
              <span className="text-slate-700 font-medium">{String(entry.new_value)}</span>
            )}
          </div>
        )}
      </div>
      <span className="text-xs text-slate-400 shrink-0 mt-1 tabular-nums">{when}</span>
    </div>
  );
}


// ──────── Master Data Tab (Sprint 1, Feb 2026 — D8) ────────
//
// SB sieht Steuer-Stammdaten des Mandanten read-only.
// Editieren nur mit Vollmacht (`permissions.edit` = true → `canEdit` Prop).
// Backend: GET /api/advisor/clients/{cid}/tax-profile + PUT (mit Vollmacht).

// lex.ivoliq (Juli 2026): Alles fürs FinanzOnline-Vollmachts-Setup auf einen Blick —
// der Haus-StB braucht den Anwalt nie zu kontaktieren.
function LexVollmachtSetupCard({ profile, clientId, onSaved }) {
  const [editStnr, setEditStnr] = useState(null); // null = zu, sonst Eingabewert
  const [stnrBusy, setStnrBusy] = useState(false);
  const fmtDate = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
    catch { return iso; }
  };
  const items = [
    ['Steuernummer', profile?.tax_number],
    ['SVNr', profile?.svnr],
    ['Geburtsdatum', fmtDate(profile?.birth_date)],
    ['UID', profile?.vat_id],
    ['IBAN', profile?.iban],
  ];
  const copy = async (label, value) => {
    try { await navigator.clipboard.writeText(value); toast.success(`${label} kopiert`); }
    catch { toast.error('Kopieren fehlgeschlagen'); }
  };
  const saveStnr = async () => {
    const err = validateStnr(editStnr);
    if (err) { toast.error(err); return; }
    setStnrBusy(true);
    try {
      await axios.post(`${API}/lex/advisor/clients/${clientId}/steuernummer`, { steuernummer: editStnr }, { headers: H() });
      toast.success('Steuernummer gespeichert.');
      setEditStnr(null);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen.');
    } finally {
      setStnrBusy(false);
    }
  };
  const hasIdent = Boolean(profile?.tax_number || (profile?.svnr && profile?.birth_date));
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4" style={{ borderLeft: '3px solid #C5A059' }} data-testid="lex-vollmacht-setup-card">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Vollmachts-Setup (lex)</div>
        {!hasIdent && (
          <span className="text-xs font-semibold text-amber-600" data-testid="lex-vollmacht-ident-warning">
            Identifikation unvollständig
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {items.map(([label, value]) => (
          <div key={label} className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2.5 min-w-0" data-testid={`lex-setup-${label.toLowerCase()}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-slate-800 font-mono truncate">{value || '—'}</span>
              {value && (
                <button type="button" onClick={() => copy(label, value)} className="text-slate-400 hover:text-slate-700 shrink-0"
                  title={`${label} kopieren`} data-testid={`lex-setup-copy-${label.toLowerCase()}`}>
                  <Copy className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {label === 'Steuernummer' && editStnr === null && (
              <button type="button" onClick={() => setEditStnr(value || '')}
                className="text-[11px] font-medium text-amber-700 hover:text-amber-900 mt-1"
                data-testid="lex-setup-stnr-edit-btn">
                {value ? 'Ändern' : 'Nachtragen'}
              </button>
            )}
          </div>
        ))}
      </div>
      {editStnr !== null && (
        <div className="mt-3 flex items-center gap-2" data-testid="lex-setup-stnr-edit-row">
          <input className="border border-zinc-300 rounded-lg px-3 py-2 text-sm font-mono w-44"
            placeholder="99 999/9999" value={editStnr} onChange={(e) => setEditStnr(e.target.value)}
            data-testid="lex-setup-stnr-input" />
          <Button size="sm" onClick={saveStnr} disabled={stnrBusy} data-testid="lex-setup-stnr-save-btn">
            {stnrBusy ? 'Speichert…' : 'Speichern'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEditStnr(null)} data-testid="lex-setup-stnr-cancel-btn">Abbrechen</Button>
          <span className="text-xs text-slate-400">Basis für Finanzamt-IBAN + QR-Code der UVA.</span>
        </div>
      )}
      <p className="text-xs text-slate-500 mt-3">
        Für die FinanzOnline-Vollmachtseintragung: Steuernummer — oder SVNr + Geburtsdatum. UID + IBAN stehen auf der Honorarnote.
      </p>
    </div>
  );
}

function MasterDataTab({ clientId, canEdit }) {
  const [profile, setProfile] = useState(null);
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pr, cc] = await Promise.all([
        axios.get(`${API}/advisor/clients/${clientId}/tax-profile`, { headers: H() }),
        axios.get(`${API}/advisor/clients/${clientId}/country-config`, { headers: H() }),
      ]);
      setProfile(pr.data);
      setCountry(cc.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Stammdaten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const onSave = async (payload) => {
    await axios.put(`${API}/advisor/clients/${clientId}/tax-profile`, payload, { headers: H() });
    await load();
  };

  if (loading) {
    return <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-sm text-slate-500">Stammdaten laden…</div>;
  }

  return (
    <div className="space-y-3" data-testid="master-data-tab">
      {!canEdit && (
        <div className="text-xs text-slate-500 px-1">
          Read-only — der Mandant hat dir keine Vollmacht erteilt. Bearbeitung erfolgt über den Mandanten oder nach Anfrage.
        </div>
      )}
      {isLexUser(profile) && <LexVollmachtSetupCard profile={profile} clientId={clientId} onSaved={load} />}
      {/* Juni 2026 — Kontaktangaben des Mandanten (read-only) */}
      <div className="bg-white border border-zinc-200 rounded-xl p-4" data-testid="master-data-contact">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Kontakt</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <a
            href={profile?.contact_email ? `mailto:${profile.contact_email}` : undefined}
            className="flex items-center gap-2.5 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2.5 hover:bg-zinc-100 transition-colors"
            data-testid="contact-email"
          >
            <Mail className="h-4 w-4 text-emerald-600 shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">E-Mail</div>
              <div className="text-sm text-slate-800 truncate">{profile?.contact_email || '—'}</div>
            </div>
          </a>
          <a
            href={profile?.contact_phone ? `tel:${profile.contact_phone}` : undefined}
            className="flex items-center gap-2.5 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2.5 hover:bg-zinc-100 transition-colors"
            data-testid="contact-phone"
          >
            <Phone className="h-4 w-4 text-emerald-600 shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Telefon</div>
              <div className="text-sm text-slate-800 truncate">{profile?.contact_phone || '—'}</div>
            </div>
          </a>
        </div>
      </div>
      <TaxProfileForm
        profile={profile}
        countryConfig={country}
        onSave={onSave}
        readOnly={!canEdit}
        hideIdentity={false}
      />
      <AdvisorVstpControl clientId={clientId} canEdit={canEdit} />
    </div>
  );
}

// ──────── Vorsteuerpauschalierung (SB-Sicht, Juni 2026) ────────
// SB sieht den Status; Aktivierung/Deaktivierung nur mit Vollmacht + Pflicht-Grund (Audit).
function AdvisorVstpControl({ clientId, canEdit }) {
  const [data, setData] = useState(null);
  const [method, setMethod] = useState('base');
  const [branchKey, setBranchKey] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/advisor/clients/${clientId}/vorsteuer-pauschalierung`, { headers: H() });
      setData(res.data);
      const cfg = res.data?.vorsteuer_pauschalierung;
      if (cfg?.enabled) { setMethod(cfg.method || 'base'); setBranchKey(cfg.branch_key || ''); }
    } catch { /* ignore */ }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  if (!data || data.country !== 'AT') return null;
  const cfg = data.vorsteuer_pauschalierung;
  const enabled = !!cfg?.enabled;

  const save = async (nextEnabled) => {
    if (!reason.trim()) { toast.error('Bitte eine Begründung angeben (GoBD).'); return; }
    setSaving(true);
    try {
      const payload = { enabled: nextEnabled, reason: reason.trim() };
      if (nextEnabled) { payload.method = method; if (method === 'branch') payload.branch_key = branchKey; }
      const res = await axios.put(`${API}/advisor/clients/${clientId}/vorsteuer-pauschalierung`, payload, { headers: H() });
      setData((d) => ({ ...d, vorsteuer_pauschalierung: res.data.vorsteuer_pauschalierung }));
      setReason('');
      toast.success('Vorsteuerpauschalierung aktualisiert');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Aktualisierung fehlgeschlagen');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4" data-testid="advisor-vstp-control">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h4 className="font-semibold text-slate-900 text-sm">🧮 Vorsteuerpauschalierung (AT)</h4>
          <p className="text-xs text-slate-500">{enabled
            ? `Aktiv — ${cfg.method === 'branch' ? 'Branche' : 'Basis § 14'}${cfg.branch_key ? ` (${cfg.branch_key})` : ''}`
            : 'Inaktiv'}</p>
        </div>
        <span className={`text-xs font-semibold ${enabled ? 'text-emerald-600' : 'text-slate-400'}`}>{enabled ? 'Aktiv' : 'Inaktiv'}</span>
      </div>

      {!canEdit ? (
        <p className="text-xs text-slate-500">Read-only — Bearbeitung erfordert Vollmacht.</p>
      ) : !data.available ? (
        <p className="text-xs text-amber-700">Nicht verfügbar (nicht für Kleinunternehmer).</p>
      ) : (
        <div className="space-y-2">
          <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="advisor-vstp-method"
            className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm">
            <option value="base">Basispauschalierung § 14 (1,8 %)</option>
            <option value="branch">Branchenpauschalierung (BGBl 627/1983)</option>
          </select>
          {method === 'branch' && (
            <select value={branchKey} onChange={(e) => setBranchKey(e.target.value)} data-testid="advisor-vstp-branch"
              className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm">
              <option value="">– Branche wählen –</option>
              {(data.regimes?.branches || []).map((b) => (
                <option key={b.key} value={b.key}>{b.label} — {b.rate} %</option>
              ))}
            </select>
          )}
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Begründung (Pflicht, GoBD)"
            data-testid="advisor-vstp-reason" className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm" />
          <div className="flex gap-2">
            <button type="button" disabled={saving || (method === 'branch' && !branchKey)} onClick={() => save(true)}
              data-testid="advisor-vstp-activate" className="rounded-full bg-emerald-600 text-white text-sm px-4 py-2 hover:bg-emerald-700 disabled:opacity-50">
              {enabled ? 'Aktualisieren' : 'Aktivieren'}
            </button>
            {enabled && (
              <button type="button" disabled={saving} onClick={() => save(false)}
                data-testid="advisor-vstp-deactivate" className="rounded-full border border-zinc-300 text-sm px-4 py-2 hover:bg-zinc-50 disabled:opacity-50">
                Deaktivieren
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
